// /admin/crm — Panel CRM unificado.
// Layout 3 columnas: Inbox + Chat + Ficha cliente con timeline 360.
// Soporta multi-canal (whatsapp / instagram / email / webchat).
// Realtime via Supabase channels.

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import Avatar from '../../components/Avatar'
import CanalLogo from '../../components/CanalLogo'
import { supabase } from '../../lib/supabase'
import { pushSupported, pushSubscribed, pushSubscribe, pushUnsubscribe, pushTest, pushReset } from '../../lib/pushNotifications'
import {
  getConversaciones, getMensajes, getFichaCliente, getTimelineCliente,
  marcarComoLeida, enviarMensaje,
  suscribirMensajesEnVivo, suscribirInboxEnVivo,
  getRespuestasRapidas, incrementarUsoRespuesta, aplicarVariables,
  asignarAgente, actualizarTags, guardarNotasInternas, cambiarEstadoConversacion,
  getEquipo, sugerirRespuesta,
  getTemplatesAprobados, buscarClientesParaCRM, aplicarTemplateVars, iniciarConversacionConTemplate,
  subirAdjuntoCRM, subirAvatarConversacion, usarMensajeComoAvatar, quitarAvatar,
  reaccionarMensaje, quitarReaccionMensaje,
  vincularConversacionACliente, desvincularConversacionDeCliente,
  getContactoDeConversacion, actualizarContacto, toggleTipoContacto, TIPOS_CONTACTO,
} from '../../lib/crmApi'
import { AutoBubble as AutoBubbleShared, AutomationsTimeline } from '../../components/crm/sharedV3'

const ESTADOS_CONV = [
  { key: 'abierta',   label: 'Abierta',   color: '#16A34A' },
  { key: 'pendiente', label: 'Pendiente', color: '#D97706' },
  { key: 'cerrada',   label: 'Cerrada',   color: '#94A3B8' },
]

const TAGS_SUGERIDOS = ['vip', 'b2b', 'follow-up', 'queja', 'cancelación', 'duda', 'pago-fallido']

const CANALES = [
  { key: 'todos',     label: 'Todos',    canal: null },
  { key: 'whatsapp',  label: 'WhatsApp', canal: 'whatsapp' },
  { key: 'instagram', label: 'Instagram',canal: 'instagram' },
]

const SEGMENTOS = [
  { key: 'todos',          label: 'Todos' },
  { key: 'cliente',        label: 'Clientes' },
  { key: 'proveedor',      label: 'Proveedores' },
  { key: 'interno',        label: 'Internos' },
  { key: 'prospecto',      label: 'Prospectos' },
  { key: 'sin_clasificar', label: 'Sin clasificar' },
]

import CRMMobile from './CRMMobile'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768)
  useEffect(() => {
    function check() { setIsMobile(window.innerWidth <= 768) }
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return isMobile
}

export default function CRM() {
  const isMobile = useIsMobile()
  if (isMobile) return <CRMMobile />
  return <CRMDesktop />
}

function CRMDesktop() {
  const [conversaciones, setConversaciones] = useState([])
  const [activa, setActiva] = useState(null)         // id de la conv seleccionada
  const [searchParams] = useSearchParams()
  const [filtroCanal, setFiltroCanal] = useState('todos')
  const [filtroSegmento, setFiltroSegmento] = useState('todos')
  // Deep-link desde dashboard: ?filtro=no_leidas o ?filtro=pendiente activan el toggle "solo no leídas"
  const [filtroNoLeidos, setFiltroNoLeidos] = useState(() => {
    const f = searchParams.get('filtro')
    return f === 'no_leidas' || f === 'pendiente'
  })
  const [busqueda, setBusqueda] = useState('')
  const [cargando, setCargando] = useState(true)
  const [mostrarNuevoChat, setMostrarNuevoChat] = useState(false)
  const [pushOn, setPushOn] = useState(false)
  const [pushBusy, setPushBusy] = useState(false)

  // Notificaciones: por defecto ON. Auto-suscribir si ya tiene permiso del browser.
  // Si la primera vez no tiene permiso, mostramos el botón para activar manualmente.
  useEffect(() => {
    if (!pushSupported()) return
    ;(async () => {
      const yaSub = await pushSubscribed()
      if (yaSub) { setPushOn(true); return }
      // Si el browser ya dio permiso antes, suscribir silencioso (sin alert si falla).
      if (Notification.permission === 'granted') {
        try {
          await pushSubscribe()
          setPushOn(true)
        } catch (e) {
          console.warn('Auto-subscribe push falló (no muestro alert, queda manual):', e?.message || e)
          setPushOn(false)
        }
      }
    })()
  }, [])

  async function togglePush() {
    setPushBusy(true)
    try {
      if (pushOn) {
        await pushUnsubscribe()
        setPushOn(false)
      } else {
        await pushSubscribe()
        setPushOn(true)
        // Disparo push de prueba para confirmar que funciona
        setTimeout(() => pushTest().catch(() => {}), 800)
      }
    } catch (e) {
      const msg = e?.message || String(e)
      // Si parece estado corrupto del browser, ofrecer reset automático
      const esEstadoCorrupto = msg.toLowerCase().includes('public key') ||
                               msg.toLowerCase().includes('service worker') ||
                               msg.toLowerCase().includes('subscription')
      if (esEstadoCorrupto && confirm(
        `${msg}\n\n` +
        '¿Querés que limpie el estado de notificaciones del navegador y reintente?\n\n' +
        'Esto va a desuscribirte, borrar el Service Worker y recargar la página.'
      )) {
        try {
          await pushReset()
          alert('✅ Estado de notificaciones limpiado.\nLa página se va a recargar.\nDespués volvé a activar las notificaciones.')
          window.location.reload()
        } catch (e2) {
          alert('No pude resetear: ' + (e2.message || e2) + '\n\nProbá: DevTools → Application → Clear site data + recargar.')
        }
      } else {
        alert('Error: ' + msg)
      }
    } finally {
      setPushBusy(false)
    }
  }

  async function handlePushReset() {
    if (!confirm(
      'Esto va a:\n' +
      '• Desuscribir el browser de notificaciones push\n' +
      '• Borrar el Service Worker\n' +
      '• Borrar caches\n' +
      '• Recargar la página\n\n' +
      'Después podés volver a activar las notificaciones.\n\n¿Continuar?'
    )) return
    setPushBusy(true)
    try {
      const r = await pushReset()
      console.log('pushReset pasos:', r.pasos)
      alert('✅ Listo. Recargando la página…')
      window.location.reload()
    } catch (e) {
      alert('Error al resetear: ' + (e.message || e))
      setPushBusy(false)
    }
  }

  // Permite deep-link desde /admin/usuarios/:id → /admin/crm?phone=598XXXXXXXX
  // (o ?ig=username). Auto-selecciona la conv que matche el contacto.
  useEffect(() => {
    if (cargando || conversaciones.length === 0) return
    const sp = new URLSearchParams(window.location.search)
    const phoneTarget = (sp.get('phone') || '').replace(/\D/g, '')
    const igTarget    = sp.get('ig') || ''
    if (!phoneTarget && !igTarget) return
    const match = conversaciones.find(c => {
      if (phoneTarget && c.phone) {
        const clean = c.phone.replace(/\D/g, '')
        return clean === phoneTarget || clean.endsWith(phoneTarget) || phoneTarget.endsWith(clean)
      }
      if (igTarget && c.ig_username) return c.ig_username.toLowerCase() === igTarget.toLowerCase()
      return false
    })
    if (match) setActiva(match.id)
    // Limpio el param para no re-seleccionar al volver
    window.history.replaceState(null, '', '/admin/crm')
  }, [cargando, conversaciones])

  // Escapa del padding del AdminLayout para que el CRM ocupe full screen sin scroll page-wide
  useEffect(() => {
    document.body.classList.add('crm-fullscreen-mode')
    return () => document.body.classList.remove('crm-fullscreen-mode')
  }, [])

  async function cargar() {
    setCargando(true)
    const list = await getConversaciones({
      canal: filtroCanal,
      segmento: filtroSegmento,
      soloNoLeidos: filtroNoLeidos,
      q: busqueda,
    })
    setConversaciones(list)
    setCargando(false)
  }

  useEffect(() => { cargar() }, [filtroCanal, filtroSegmento, filtroNoLeidos, busqueda])

  // Realtime: cuando hay cambios en cualquier conversación, refrescamos la lista
  useEffect(() => {
    const ch = suscribirInboxEnVivo(() => cargar())
    return () => { ch.unsubscribe?.() }
  }, [filtroCanal, filtroSegmento, filtroNoLeidos, busqueda])

  const total      = conversaciones.length
  const pendientes = conversaciones.filter(c => c.unread_count > 0).length

  // Cuento conversaciones donde MI agente respondió HOY (cualquier momento, no solo el último mensaje)
  const [respondidasHoy, setRespondidasHoy] = useState(0)
  useEffect(() => {
    (async () => {
      const hoy = new Date(); hoy.setHours(0,0,0,0)
      const { count } = await supabase
        .from('messages')
        .select('conversation_id', { count: 'exact', head: false })
        .eq('direccion', 'out')
        .not('agente_id', 'is', null)
        .gte('created_at', hoy.toISOString())
      setRespondidasHoy(count || 0)
    })()
  }, [conversaciones.length])

  // Conteos para filtros
  const cntCanal = {
    todos: total,
    whatsapp: conversaciones.filter(c => c.canal === 'whatsapp').length,
    instagram: conversaciones.filter(c => c.canal === 'instagram').length,
    email: conversaciones.filter(c => c.canal === 'email').length,
  }
  const cntSeg = {
    todos: total,
    suscriptor: conversaciones.filter(c => !!c.cliente_id).length,
    visitante: conversaciones.filter(c => !c.cliente_id).length,
    b2b: conversaciones.filter(c => (c.tags || []).includes('b2b')).length,
  }

  const [nuevaConvOpen, setNuevaConvOpen] = useState(false)

  return (
    <AdminLayout>
      <Estilos />
      <div className="crm-wrap">
        {/* Top bar con titulo + stats inline (estilo dashboard) */}
        <header className="crm-topbar">
          <div className="crm-topbar-title">
            <h1>CRM</h1>
            <span className="crm-topbar-tag">Multi-canal</span>
          </div>
          <div className="crm-topbar-stats">
            <div className="crm-stat">
              <span className="crm-stat-dot crm-stat-dot-warn" />
              <strong>{pendientes}</strong> pendientes
            </div>
            <div className="crm-stat">
              <span className="crm-stat-dot crm-stat-dot-ok" />
              <strong>{respondidasHoy}</strong> respondidas hoy
            </div>
            <div className="crm-stat">
              ⏱ <strong>4 min</strong> medio
            </div>
            <div className="crm-stat">
              💬 <strong>{total}</strong> total
            </div>
            <button className="crm-topbar-cta" onClick={() => setNuevaConvOpen(true)} title="Iniciar conversación nueva con un cliente">
              + Nueva
            </button>
          </div>
        </header>

        {nuevaConvOpen && (
          <NuevaConvModal
            onClose={() => setNuevaConvOpen(false)}
            onCreated={(convId) => {
              setNuevaConvOpen(false)
              setActiva(convId)
              cargar()
            }}
          />
        )}

        <div className={`crm-body ${activa ? 'has-active' : ''}`}>
        <aside className="crm-inbox">
          <header className="crm-inbox-head">
            <div className="crm-inbox-title">
              <span>Bandeja</span>
              {pendientes > 0 && <span className="crm-badge-pend">{pendientes}</span>}
              {pushSupported() && (
                <>
                  <button
                    onClick={togglePush}
                    disabled={pushBusy}
                    title={pushOn ? 'Notificaciones activas. Click para apagarlas.' : 'Activar notificaciones push para no perder mensajes en mobile.'}
                    style={{ marginLeft: 'auto', padding: '4px 8px', background: pushOn ? '#dcfce7' : '#fff', color: pushOn ? '#166534' : '#475569', border: '1px solid ' + (pushOn ? '#86efac' : '#cbd5e1'), borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: pushBusy ? 'wait' : 'pointer', fontFamily: 'inherit' }}
                  >{pushBusy ? '⏳' : pushOn ? '🔔 Activas · Apagar' : '🔕 Activar'}</button>
                  <button
                    onClick={handlePushReset}
                    disabled={pushBusy}
                    title="Resetear estado push (usar si las notificaciones no se activan o aparece error 'public key')"
                    style={{ marginLeft: 4, padding: '4px 6px', background: '#fff', color: '#64748B', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: pushBusy ? 'wait' : 'pointer', fontFamily: 'inherit' }}
                  >↻</button>
                </>
              )}
              <button
                onClick={() => setMostrarNuevoChat(true)}
                title="Iniciar chat con cliente nuevo (usa template aprobada por Meta)"
                style={{ marginLeft: pushSupported() ? 6 : 'auto', padding: '4px 10px', background: '#1a3461', color: '#fff', border: 0, borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
              >+ Nuevo chat</button>
            </div>
            <input
              className="crm-search"
              type="search"
              placeholder="Buscar nombre, teléfono o mensaje…"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
            <div className="crm-filtros">
              {CANALES.map(c => (
                <button
                  key={c.key}
                  className={`crm-filtro ${filtroCanal === c.key ? 'is-active' : ''}`}
                  onClick={() => setFiltroCanal(c.key)}
                >
                  {c.canal
                    ? <CanalLogo canal={c.canal} size={14} />
                    : <span style={{ fontSize: 13 }}>💬</span>}
                  {c.label}
                  <span className="crm-filtro-count">{cntCanal[c.key] ?? 0}</span>
                </button>
              ))}
            </div>
            <div className="crm-filtros">
              {SEGMENTOS.map(s => (
                <button
                  key={s.key}
                  className={`crm-pill ${filtroSegmento === s.key ? 'is-active' : ''}`}
                  onClick={() => setFiltroSegmento(s.key)}
                >
                  {s.label} <span className="crm-pill-count">{cntSeg[s.key] ?? 0}</span>
                </button>
              ))}
              <button
                className={`crm-pill crm-pill-pend ${filtroNoLeidos ? 'is-active' : ''}`}
                onClick={() => setFiltroNoLeidos(v => !v)}
              >
                ● Pendientes <span className="crm-pill-count">{pendientes}</span>
              </button>
            </div>
          </header>

          <div className="crm-inbox-list">
            {cargando ? (
              <InboxSkeleton />
            ) : conversaciones.length === 0 ? (
              <div className="crm-empty">
                <div className="crm-empty-icon">📭</div>
                <div>Todo limpio por acá</div>
                <div className="crm-empty-sub">Cuando alguien escriba a Limpack, va a aparecer en esta bandeja. Capaz es buen momento para una mate.</div>
              </div>
            ) : (
              conversaciones.map(conv => (
                <ConversacionItem
                  key={conv.id}
                  conv={conv}
                  activa={activa === conv.id}
                  onClick={() => {
                    setActiva(conv.id)
                    if (conv.unread_count > 0) marcarComoLeida(conv.id).then(() => cargar())
                  }}
                />
              ))
            )}
          </div>
          <div className="crm-inbox-stats">
            <span>{total} conversaciones</span>
            <span className="crm-sep">·</span>
            <span>{pendientes} pendientes</span>
          </div>
        </aside>

        <main className="crm-chat">
          {activa ? (
            <ChatView
              conversationId={activa}
              onCerrarConv={() => setActiva(null)}
              onSeleccionar={(id) => setActiva(id)}
            />
          ) : (
            <div className="crm-chat-empty">
              <div className="crm-chat-empty-icon">💬</div>
              <div className="crm-chat-empty-h">Seleccioná una conversación</div>
              <div className="crm-chat-empty-sub">El chat va a aparecer en esta área. La info del cliente sale en el panel de la derecha.</div>
            </div>
          )}
        </main>

        <aside className="crm-cliente">
          {activa ? (
            <FichaPanel conversationId={activa} />
          ) : (
            <div className="crm-cliente-empty">
              <div className="crm-empty-icon">👤</div>
              <div>Acá va la ficha del cliente</div>
            </div>
          )}
        </aside>
        </div>

        {mostrarNuevoChat && (
          <ModalNuevoChat
            onClose={() => setMostrarNuevoChat(false)}
            onCreado={(convId) => {
              setMostrarNuevoChat(false)
              cargar().then(() => setActiva(convId))
            }}
          />
        )}
      </div>
    </AdminLayout>
  )
}

/* ─────────── Componentes ─────────── */

function InboxSkeleton() {
  return (
    <div className="crm-skel">
      {[0,1,2,3,4].map(i => (
        <div className="crm-skel-item" key={i} style={{ animationDelay: `${i * 0.08}s` }}>
          <div className="crm-skel-avatar" />
          <div className="crm-skel-body">
            <div className="crm-skel-line crm-skel-line-lg" />
            <div className="crm-skel-line crm-skel-line-md" />
            <div className="crm-skel-line crm-skel-line-sm" />
          </div>
        </div>
      ))}
    </div>
  )
}

function ConversacionItem({ conv, activa, onClick }) {
  const canalBadge = <CanalLogo canal={conv.canal} size={16} />

  const dotColor = {
    cliente: '#16A34A',
    agente: '#0EA5E9',
    sistema: '#94A3B8',
  }[conv.last_message_from] || '#94A3B8'

  const nombre = conv.nombre_visible || conv.cliente?.alias || conv.phone || conv.ig_username || 'Sin nombre'
  const subtitulo = conv.cliente?.kit?.nombre
    ? `${conv.cliente.kit.nombre}${conv.cliente.barrio ? ' · ' + conv.cliente.barrio : ''}`
    : conv.cliente_id ? 'Cliente Limpack' : 'Visitante'

  return (
    <button className={`crm-conv ${activa ? 'is-active' : ''} ${conv.unread_count > 0 ? 'is-unread' : ''} ${conv._matchHistorico ? 'is-historic-match' : ''}`} onClick={onClick}>
      <div className="crm-conv-avatar">
        <Avatar
          foto={conv.foto_perfil_url}
          seed={conv.phone || conv.ig_username || conv.id}
          size={44}
          badge={canalBadge}
        />
        {conv.agente?.nombre && (
          <span className="crm-conv-assignee" title={`Asignado a ${conv.agente.nombre}`}>
            {iniciales(conv.agente.nombre)}
          </span>
        )}
        {conv._matchHistorico && (
          <span className="crm-conv-historic" title="Coincidencia en mensajes anteriores">🔍</span>
        )}
      </div>
      <div className="crm-conv-body">
        <div className="crm-conv-top">
          <div className="crm-conv-nombre">{nombre}</div>
          <div className="crm-conv-tiempo">{fmtTiempoCorto(conv.last_message_at)}</div>
        </div>
        <div className="crm-conv-sub">{subtitulo}</div>
        <div className="crm-conv-preview">
          <span className="crm-conv-dot" style={{ background: dotColor }} />
          {conv.last_message_preview || 'Sin mensajes'}
        </div>
        {((conv.tags?.length || 0) > 0 || (conv.auto_tags?.length || 0) > 0) && (
          <div className="crm-conv-tags">
            {(conv.auto_tags || []).slice(0, 3).map(t => (
              <span key={`a-${t}`} className={`crm-conv-tag crm-conv-tag-auto crm-auto-tag-${t}`}>#{t}</span>
            ))}
            {(conv.tags || []).slice(0, 3).map(t => <span key={`u-${t}`} className="crm-conv-tag">#{t}</span>)}
          </div>
        )}
      </div>
      {conv.unread_count > 0 && (
        <div className="crm-conv-unread">{conv.unread_count}</div>
      )}
    </button>
  )
}

// Borradores persistentes del composer — uno por conversación.
// Sobrevive a navegación entre conversaciones, cambio de pantalla, refresh y reinicio del navegador.
const BORRADOR_KEY = (convId) => `crm_borrador_${convId}`
function leerBorrador(convId) {
  if (!convId) return ''
  try { return localStorage.getItem(BORRADOR_KEY(convId)) || '' }
  catch { return '' }
}
function guardarBorrador(convId, texto) {
  if (!convId) return
  try {
    if (texto && texto.length > 0) localStorage.setItem(BORRADOR_KEY(convId), texto)
    else localStorage.removeItem(BORRADOR_KEY(convId))
  } catch { /* localStorage lleno / privado */ }
}

function ChatView({ conversationId, onCerrarConv, onSeleccionar }) {
  const [mensajes, setMensajes] = useState([])
  const [conv, setConv] = useState(null)
  const [otrasConvs, setOtrasConvs] = useState([]) // misma persona, otros canales
  const [ficha, setFicha] = useState(null)
  // Borrador inicializa con lo guardado en localStorage (sobrevive a refresh + cambio de pantalla)
  const [borrador, setBorrador] = useState(() => leerBorrador(conversationId))
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState(null)
  // Respuestas rápidas
  const [respuestas, setRespuestas] = useState([])
  const [slashOpen, setSlashOpen] = useState(false)
  const [slashFiltro, setSlashFiltro] = useState('')
  const [slashIndex, setSlashIndex] = useState(0)
  // Equipo + popovers nuevos
  const [equipo, setEquipo] = useState([])
  const [asigneeOpen, setAsigneeOpen] = useState(false)
  const [estadoOpen, setEstadoOpen] = useState(false)
  const [notasOpen, setNotasOpen] = useState(false)
  const [tagsOpen, setTagsOpen] = useState(false)
  const [notasBorrador, setNotasBorrador] = useState('')
  const [notasGuardando, setNotasGuardando] = useState(false)
  const [nuevoTag, setNuevoTag] = useState('')
  // F3: sugerencia Claude
  const [sugerencia, setSugerencia] = useState(null) // { sugerencia, confianza, etiqueta }
  const [sugiriendo, setSugiriendo] = useState(false)
  const [sugerenciaError, setSugerenciaError] = useState(null)
  const ultimoSugeridoRef = useRef(null) // id del último mensaje sobre el que sugerimos
  // Adjuntos
  const [subiendoAdjunto, setSubiendoAdjunto] = useState(false)
  const [adjuntoPreview, setAdjuntoPreview] = useState(null) // { file, url, tipoWA, captionDraft }
  // Modal de templates (cuando ventana 24h está cerrada)
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)
  const fileInputRef = useRef(null)

  async function cargarTodo() {
    const list = await getConversaciones()  // ineficiente pero alcanza para v1
    const c = list.find(x => x.id === conversationId)
    setConv(c || null)
    const msgs = await getMensajes(conversationId)
    setMensajes(msgs)
    // Otras conversaciones de la misma persona (mismo cliente_id, otro canal)
    // Útil cuando un cliente nos escribe por WhatsApp Y por Instagram.
    if (c?.cliente_id) {
      const otras = list.filter(x => x.id !== c.id && x.cliente_id === c.cliente_id)
      setOtrasConvs(otras)
      getFichaCliente(c.cliente_id).then(setFicha)
    } else {
      setOtrasConvs([])
      setFicha(null)
    }
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50)
  }

  useEffect(() => { cargarTodo() }, [conversationId])

  // Cargar respuestas rápidas + equipo una sola vez
  useEffect(() => {
    getRespuestasRapidas().then(setRespuestas)
    getEquipo().then(setEquipo)
  }, [])

  // Cuando cambia conv, sincronizar borrador notas
  useEffect(() => {
    setNotasBorrador(conv?.notas_internas || '')
  }, [conv?.id])

  // ── Borrador del composer: persiste en localStorage por conversation_id ──
  // Al cambiar de conversación, restaurar el borrador guardado de la nueva conv.
  useEffect(() => {
    setBorrador(leerBorrador(conversationId))
  }, [conversationId])

  // Al cambiar el texto del borrador, guardar en localStorage para esta conversación.
  useEffect(() => {
    guardarBorrador(conversationId, borrador)
  }, [borrador, conversationId])

  // Cerrar popovers cuando se clickea afuera
  useEffect(() => {
    function onDocClick(e) {
      if (!e.target.closest('.crm-popover-wrap') && !e.target.closest('.crm-chat-tags')) {
        setAsigneeOpen(false); setEstadoOpen(false); setNotasOpen(false); setTagsOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  // Helpers para actualizar conv localmente tras una acción
  function refrescarConv(parche) {
    setConv(prev => prev ? { ...prev, ...parche } : prev)
  }

  async function handleAsignar(profileId) {
    const previo = conv?.agente_asignado
    refrescarConv({ agente_asignado: profileId, agente: equipo.find(p => p.id === profileId) || null })
    setAsigneeOpen(false)
    try { await asignarAgente(conversationId, profileId) }
    catch (e) { setError(e.message); refrescarConv({ agente_asignado: previo }) }
  }

  async function handleEstado(nuevo) {
    const previo = conv?.estado
    refrescarConv({ estado: nuevo })
    setEstadoOpen(false)
    try { await cambiarEstadoConversacion(conversationId, nuevo) }
    catch (e) { setError(e.message); refrescarConv({ estado: previo }) }
  }

  async function handleAgregarTag(t) {
    const tag = t.trim().toLowerCase()
    if (!tag) return
    const actuales = conv?.tags || []
    if (actuales.includes(tag)) return
    const nuevos = [...actuales, tag]
    refrescarConv({ tags: nuevos })
    setNuevoTag('')
    try { await actualizarTags(conversationId, nuevos) }
    catch (e) { setError(e.message); refrescarConv({ tags: actuales }) }
  }

  async function handleQuitarTag(t) {
    const actuales = conv?.tags || []
    const nuevos = actuales.filter(x => x !== t)
    refrescarConv({ tags: nuevos })
    try { await actualizarTags(conversationId, nuevos) }
    catch (e) { setError(e.message); refrescarConv({ tags: actuales }) }
  }

  async function handleGuardarNotas() {
    setNotasGuardando(true)
    try {
      await guardarNotasInternas(conversationId, notasBorrador)
      refrescarConv({ notas_internas: notasBorrador })
      setNotasOpen(false)
    } catch (e) { setError(e.message) }
    finally { setNotasGuardando(false) }
  }

  // Quick actions del chat — rellenan el composer con plantillas contextuales
  function aplicarQuickAction(kind) {
    const nombre = (conv?.nombre_visible || ficha?.titular?.nombre || '').split(' ')[0]
    const saludo = nombre ? `Hola ${nombre}` : 'Hola'
    const kit = ficha?.kit?.nombre || 'tu kit'
    const dia = ficha?.dia_entrega || 'tu día de entrega'
    const proxFecha = ficha?.kpis?.proxima_entrega?.fecha_programada
    const proxFechaTxt = proxFecha
      ? new Date(proxFecha + 'T12:00:00').toLocaleDateString('es-UY', { day: 'numeric', month: 'long' })
      : 'tu próxima fecha programada'
    const orden = ficha?.kpis?.proxima_entrega?.numero_orden

    let texto = ''
    switch (kind) {
      case 'reprogramar':
        texto = `${saludo}, queríamos coordinar reprogramar tu entrega ${orden ? `(${orden}) ` : ''}del ${proxFechaTxt}. ¿Te queda mejor algún otro día de esta semana?`
        break
      case 'disculpas':
        texto = `${saludo}, te pedimos disculpas por lo que pasó 🙏. Para compensarte, te dejamos un crédito en tu próxima entrega. ¿Hay algo más en lo que te podamos ayudar?`
        break
      case 'pausar':
        texto = `${saludo}, te confirmamos que pausamos tu suscripción de ${kit} por 1 mes. Tu próxima entrega quedaría para el mes siguiente. Si querés cambiar la fecha o cancelar la pausa, avisanos cuando quieras.`
        break
      case 'repartidor':
        texto = orden
          ? `${saludo}, te paso info de tu próxima entrega:\n\n📦 ${kit}\n📅 ${proxFechaTxt}\n📍 ${ficha?.direccion || dia}\n\nCualquier cosa que necesites coordinar, escribime.`
          : `${saludo}, no tenés ninguna entrega programada por ahora. Si querés coordinar una nueva, avisame.`
        break
      default:
        return
    }
    setBorrador(texto)
    setTimeout(() => {
      inputRef.current?.focus()
      const el = inputRef.current
      if (el) { el.selectionStart = el.selectionEnd = texto.length }
    }, 30)
  }

  // F3: pedir sugerencia a Claude
  async function pedirSugerencia({ silencioso = false } = {}) {
    if (sugiriendo) return
    setSugiriendo(true)
    if (!silencioso) setSugerenciaError(null)
    try {
      const r = await sugerirRespuesta(conversationId)
      if (r?.sugerencia) {
        setSugerencia(r)
      } else if (!silencioso) {
        setSugerenciaError(r?.motivo || 'Sin sugerencia disponible')
      }
    } catch (e) {
      if (!silencioso) setSugerenciaError(e.message)
    } finally {
      setSugiriendo(false)
    }
  }

  function usarSugerencia() {
    if (!sugerencia?.sugerencia) return
    setBorrador(sugerencia.sugerencia)
    setSugerencia(null)
    setTimeout(() => inputRef.current?.focus(), 30)
  }

  function descartarSugerencia() {
    setSugerencia(null); setSugerenciaError(null)
  }

  // Claude se invoca SOLO a demanda con el botón ✨ del composer.
  // (Antes había auto-trigger al recibir mensaje inbound — sacado por pedido del operador
  //  para evitar consumo innecesario y dejar el control 100% manual.)

  // Realtime: escuchar nuevos mensajes de esta conversación
  useEffect(() => {
    const ch = suscribirMensajesEnVivo(conversationId, payload => {
      if (payload.eventType === 'INSERT') {
        setMensajes(prev => [...prev, payload.new])
        setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50)
      } else if (payload.eventType === 'UPDATE') {
        setMensajes(prev => prev.map(m => m.id === payload.new.id ? { ...m, ...payload.new } : m))
      }
    })
    return () => { ch.unsubscribe?.() }
  }, [conversationId])

  async function handleEnviar() {
    if (!borrador.trim() || enviando) return
    setError(null)
    setEnviando(true)
    try {
      await enviarMensaje({
        conversation_id: conversationId,
        canal: conv?.canal,
        tipo: 'text',
        texto: borrador.trim(),
      })
      setBorrador('')
      // El useEffect ya limpia localStorage al setBorrador(''), pero forzamos
      // por si el componente se desmonta antes de que React procese el effect.
      guardarBorrador(conversationId, '')
      setSlashOpen(false)
    } catch (e) {
      setError(e.message)
    } finally {
      setEnviando(false)
    }
  }

  // Helper compartido por handleAdjuntar (botón 📎) y handlePaste (cmd+V):
  // arma el preview a partir de un File. Centraliza la lógica para no duplicar.
  function previewearArchivo(fileOriginal) {
    if (!fileOriginal || subiendoAdjunto) return
    setError(null)

    const mime = fileOriginal.type || 'application/octet-stream'
    let tipoWA = 'document'
    if (mime.startsWith('image/')) tipoWA = 'image'
    else if (mime.startsWith('audio/')) tipoWA = 'audio'
    else if (mime.startsWith('video/')) tipoWA = 'video'

    // Para imágenes pegadas con cmd+V, el browser pasa el filename como "image.png"
    // genérico o vacío. Reconstruimos un File con nombre útil para que el storage no
    // pise archivos y wa-reply mande un filename razonable a Meta.
    let file = fileOriginal
    const nombreOk = fileOriginal.name && !['image.png', 'image.jpg', 'image.jpeg', ''].includes(fileOriginal.name)
    if (!nombreOk && tipoWA === 'image') {
      const ext = (mime.split('/')[1] || 'png').split(';')[0]
      const nombreNuevo = `paste-${Date.now()}.${ext}`
      try {
        file = new File([fileOriginal], nombreNuevo, { type: mime })
      } catch {
        // Algunos browsers antiguos no soportan new File. En ese caso seguimos con el original.
        file = fileOriginal
      }
    }

    // Para preview de imagen/video usamos URL.createObjectURL (no sube nada hasta confirmar)
    const previewUrl = (tipoWA === 'image' || tipoWA === 'video')
      ? URL.createObjectURL(file)
      : null

    setAdjuntoPreview({
      file,
      previewUrl,
      tipoWA,
      mime,
      filename: file.name,
      sizeKb: Math.round(file.size / 1024),
      captionDraft: borrador.trim(),
    })
  }

  // Adjuntar archivo desde el botón 📎
  function handleAdjuntar(e) {
    const file = e.target.files?.[0]
    previewearArchivo(file)
    // limpiamos el input para poder re-elegir el mismo archivo si lo cancela
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // Pegar imagen con cmd+V (screenshot, copy de web, etc.)
  // Solo intercepta si hay una imagen en el clipboard. Texto plano sigue funcionando como siempre.
  function handlePaste(e) {
    if (!ventanaAbierta || subiendoAdjunto) return
    const items = e.clipboardData?.items || []
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) {
          e.preventDefault()
          previewearArchivo(file)
          return
        }
      }
    }
    // Si no había imagen, dejamos que el textarea reciba el paste normal (texto)
  }

  async function confirmarEnvioAdjunto(captionFinal) {
    if (!adjuntoPreview) return
    setError(null)
    setSubiendoAdjunto(true)
    try {
      const { url, mime, filename, tipoWA } = await subirAdjuntoCRM(adjuntoPreview.file, conversationId)
      const payload = {
        conversation_id: conversationId,
        canal: conv?.canal,
        tipo: tipoWA,
        media_url: url,
        media_mime: mime,
        media_filename: filename,
      }
      if (captionFinal && captionFinal.trim()) {
        payload.texto = captionFinal.trim()
      }
      await enviarMensaje(payload)
      setBorrador('')
      cerrarPreviewAdjunto()
    } catch (err) {
      setError('No se pudo adjuntar: ' + err.message)
    } finally {
      setSubiendoAdjunto(false)
    }
  }

  function cerrarPreviewAdjunto() {
    if (adjuntoPreview?.previewUrl) URL.revokeObjectURL(adjuntoPreview.previewUrl)
    setAdjuntoPreview(null)
  }

  // Detección del slash command. Si el texto del input comienza con "/" o "/loquesea", abre dropdown.
  function onBorradorChange(e) {
    const val = e.target.value
    setBorrador(val)
    const m = val.match(/^\/(\w*)$/)
    if (m) {
      setSlashOpen(true)
      setSlashFiltro(m[1].toLowerCase())
      setSlashIndex(0)
    } else {
      setSlashOpen(false)
    }
  }

  // Lista filtrada de respuestas según lo que viene después del /
  const respuestasFiltradas = slashOpen
    ? respuestas.filter(r =>
        r.slug.toLowerCase().includes(slashFiltro) ||
        r.titulo.toLowerCase().includes(slashFiltro)
      ).slice(0, 8)
    : []

  function aplicarRespuesta(r) {
    const ctx = { conv, ficha }
    const textoConVars = aplicarVariables(r.texto, ctx)
    setBorrador(textoConVars)
    setSlashOpen(false)
    incrementarUsoRespuesta(r.id).catch(() => {})
    // Foco al final del input
    setTimeout(() => {
      inputRef.current?.focus()
      const el = inputRef.current
      if (el) { el.selectionStart = el.selectionEnd = textoConVars.length }
    }, 30)
  }

  function onKeyDown(e) {
    // Navegación del dropdown
    if (slashOpen && respuestasFiltradas.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSlashIndex(i => Math.min(i + 1, respuestasFiltradas.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSlashIndex(i => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        aplicarRespuesta(respuestasFiltradas[slashIndex])
        return
      }
      if (e.key === 'Escape') {
        setSlashOpen(false)
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleEnviar()
    }
  }

  const ventanaAbierta = conv?.ventana_24h_hasta && new Date(conv.ventana_24h_hasta) > new Date()
  const nombre = conv?.nombre_visible || conv?.cliente?.alias || conv?.phone || '—'
  // "En línea" = mensajes recibidos en los últimos 5 minutos
  const ultimoInbound = mensajes.filter(m => m.direccion === 'in').slice(-1)[0]
  const enLinea = ultimoInbound && (new Date() - new Date(ultimoInbound.created_at)) < 5 * 60 * 1000

  return (
    <>
      <header className="crm-chat-head">
        <button className="crm-back" onClick={onCerrarConv} title="Volver">←</button>
        <Avatar
          foto={conv?.foto_perfil_url}
          seed={conv?.phone || conv?.ig_username || conv?.id || nombre}
          size={48}
          badge={conv?.canal ? <CanalLogo canal={conv.canal} size={20} /> : null}
        />
        <div className="crm-chat-head-body">
          <div className="crm-chat-head-name">
            {nombre}
            {enLinea && <span className="crm-online-pill">en línea</span>}
          </div>
          <div className="crm-chat-head-meta">
            {/* Canal con identificador. Para WA: +598... · Para IG: @username */}
            {conv?.canal && (
              <span className="crm-canal-id" title={canalLabel(conv.canal)}>
                <CanalLogo canal={conv.canal} size={14} />
                <span className="crm-canal-id-text">
                  {conv.canal === 'whatsapp' && conv?.phone && `+${conv.phone}`}
                  {conv.canal === 'instagram' && (conv?.ig_username ? `@${conv.ig_username}` : 'Instagram')}
                  {conv.canal === 'email' && (conv?.phone || 'Email')}
                  {conv.canal === 'webchat' && 'Webchat'}
                </span>
              </span>
            )}
            <span className="crm-meta-sep">·</span>
            {conv?.cliente_id ? <span>Suscriptor activo</span> : <span>Visitante</span>}
            {/* Otras conversaciones de la misma persona (otro canal) */}
            {otrasConvs.length > 0 && (
              <>
                <span className="crm-meta-sep">·</span>
                <span className="crm-otras-label">también por</span>
                {otrasConvs.map(o => (
                  <button
                    key={o.id}
                    className="crm-otra-conv-pill"
                    onClick={() => onSeleccionar && onSeleccionar(o.id)}
                    title={`Abrir conversación de ${canalLabel(o.canal)}`}
                  >
                    <CanalLogo canal={o.canal} size={12} />
                    <span>{canalLabel(o.canal)}</span>
                  </button>
                ))}
              </>
            )}
          </div>
          {/* Tags chips: auto-tags (read-only) + user-tags (clickables) */}
          <div className="crm-chat-tags">
            {(conv?.auto_tags || []).map(t => (
              <span key={`a-${t}`} className={`crm-tag-chip crm-tag-chip-auto crm-auto-tag-${t}`}
                    title="Tag automático del sistema">
                #{t}
              </span>
            ))}
            {(conv?.tags || []).map(t => (
              <span key={t} className="crm-tag-chip" onClick={() => handleQuitarTag(t)} title="Click para quitar">
                #{t} <span className="crm-tag-x">×</span>
              </span>
            ))}
            <button className="crm-tag-add" onClick={() => setTagsOpen(v => !v)} title="Agregar etiqueta">+ tag</button>
            {tagsOpen && (
              <div className="crm-popover crm-popover-tags" onClick={e => e.stopPropagation()}>
                <input
                  className="crm-popover-input"
                  placeholder="Nueva etiqueta…"
                  value={nuevoTag}
                  onChange={e => setNuevoTag(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAgregarTag(nuevoTag) }}
                  autoFocus
                />
                <div className="crm-popover-label">Sugeridas</div>
                <div className="crm-popover-tags-grid">
                  {TAGS_SUGERIDOS.filter(t => !(conv?.tags || []).includes(t)).map(t => (
                    <button key={t} className="crm-tag-sugerido" onClick={() => handleAgregarTag(t)}>#{t}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="crm-chat-head-actions">
          {ventanaAbierta ? (
            <span className="crm-pill-ventana crm-pill-ventana-ok">Ventana 24h</span>
          ) : (
            <span className="crm-pill-ventana crm-pill-ventana-cerrada">Solo template</span>
          )}

          {/* Asignar agente */}
          <div className="crm-popover-wrap">
            <button
              className={`crm-assignee-btn ${conv?.agente_asignado ? 'is-assigned' : ''}`}
              onClick={() => setAsigneeOpen(v => !v)}
              title={conv?.agente?.nombre ? `Asignado a ${conv.agente.nombre}` : 'Sin asignar'}
            >
              {conv?.agente?.nombre ? (
                <span className="crm-assignee-avatar">{iniciales(conv.agente.nombre)}</span>
              ) : (
                <span className="crm-assignee-empty">＋</span>
              )}
            </button>
            {asigneeOpen && (
              <div className="crm-popover crm-popover-assignee" onClick={e => e.stopPropagation()}>
                <div className="crm-popover-label">Asignar a</div>
                <button className="crm-popover-row" onClick={() => handleAsignar(null)}>
                  <span className="crm-assignee-empty">∅</span> Sin asignar
                </button>
                {equipo.map(p => (
                  <button key={p.id} className={`crm-popover-row ${conv?.agente_asignado === p.id ? 'is-current' : ''}`}
                    onClick={() => handleAsignar(p.id)}>
                    <span className="crm-assignee-avatar">{iniciales(p.nombre)}</span>
                    <span>{p.nombre}</span>
                    <span className="crm-popover-role">{p.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Estado */}
          <div className="crm-popover-wrap">
            {(() => {
              const est = ESTADOS_CONV.find(e => e.key === conv?.estado) || ESTADOS_CONV[0]
              return (
                <button className="crm-estado-btn" onClick={() => setEstadoOpen(v => !v)}
                  style={{ borderColor: est.color, color: est.color }}>
                  <span className="crm-estado-dot" style={{ background: est.color }} />
                  {est.label}
                </button>
              )
            })()}
            {estadoOpen && (
              <div className="crm-popover crm-popover-estado" onClick={e => e.stopPropagation()}>
                {ESTADOS_CONV.map(e => (
                  <button key={e.key} className="crm-popover-row" onClick={() => handleEstado(e.key)}>
                    <span className="crm-estado-dot" style={{ background: e.color }} />
                    {e.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Notas */}
          <div className="crm-popover-wrap">
            <button
              className={`crm-icon-btn ${conv?.notas_internas ? 'has-content' : ''}`}
              onClick={() => setNotasOpen(v => !v)}
              title={conv?.notas_internas ? 'Notas internas (con contenido)' : 'Notas internas'}
            >📋{conv?.notas_internas ? <span className="crm-icon-dot" /> : null}</button>
            {notasOpen && (
              <div className="crm-popover crm-popover-notas" onClick={e => e.stopPropagation()}>
                <div className="crm-popover-label">Notas internas (no las ve el cliente)</div>
                <textarea
                  className="crm-popover-textarea"
                  rows={6}
                  placeholder="Ej: prefiere entrega después de las 17h, vive en PH al fondo, paga con OCA…"
                  value={notasBorrador}
                  onChange={e => setNotasBorrador(e.target.value)}
                />
                <div className="crm-popover-actions">
                  <button className="crm-popover-btn-ghost" onClick={() => { setNotasBorrador(conv?.notas_internas || ''); setNotasOpen(false) }}>Cancelar</button>
                  <button className="crm-popover-btn-primary" onClick={handleGuardarNotas} disabled={notasGuardando}>
                    {notasGuardando ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* F4: Banner pinned por tags importantes (user-tags + auto-tags) */}
      {(() => {
        const combinados = [...(conv?.tags || []), ...(conv?.auto_tags || [])]
        return combinados.some(t => ['vip','queja','cancelación','cancelada','pago-fallido','cobro-vencido'].includes(t)) ? (
          <PinnedBanner tags={combinados} />
        ) : null
      })()}

      <div className="crm-chat-msgs" ref={scrollRef}>
        {mensajes.length === 0 ? (
          <div className="crm-msg-empty">Sin mensajes en esta conversación todavía.</div>
        ) : (
          renderMensajesConDias(mensajes, conversationId, conv?.canal)
        )}
      </div>

      {error && (
        <div className="crm-error-bar">
          <span>⚠ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Quick actions arriba del composer (templates pre-aprobadas + atajos) */}
      <div className="crm-quick">
        <button className="crm-qr crm-qr-tmpl" onClick={() => aplicarQuickAction('reprogramar')}>
          ⚡ Reprogramar entrega
        </button>
        <button className="crm-qr crm-qr-tmpl" onClick={() => aplicarQuickAction('disculpas')}>
          ⚡ Disculpas + crédito
        </button>
        <button className="crm-qr crm-qr-tmpl" onClick={() => aplicarQuickAction('pausar')}>
          ⚡ Pausar 1 mes
        </button>
        <button className="crm-qr"
          onClick={() => {
            if (!conv?.phone) return
            window.open(`tel:+${conv.phone}`, '_self')
          }}
          disabled={!conv?.phone}
          title={conv?.phone ? `Llamar a +${conv.phone}` : 'Sin teléfono'}
        >📞 Llamar</button>
        <button className="crm-qr" onClick={() => aplicarQuickAction('repartidor')}>
          🚚 Próxima entrega
        </button>
      </div>

      {/* F3: Sugerencia Claude */}
      {(sugerencia || sugiriendo || sugerenciaError) && (
        <SugerenciaCard
          sugerencia={sugerencia}
          sugiriendo={sugiriendo}
          error={sugerenciaError}
          onUsar={usarSugerencia}
          onDescartar={descartarSugerencia}
        />
      )}

      <footer className="crm-composer">
        {!ventanaAbierta ? (
          /* Ventana 24h cerrada → CTA grande para abrir el modal de templates,
             con el contacto pre-cargado (no hay que volver a buscarlo). */
          <button
            className="crm-composer-template-cta"
            onClick={() => setTemplateModalOpen(true)}
            title="Iniciar conversación con un template aprobado por Meta"
          >
            <span className="crm-composer-template-icon">📨</span>
            <span className="crm-composer-template-label">
              <strong>Mandar template a {conv?.nombre_visible || (conv?.phone ? '+' + conv.phone : 'este contacto')}</strong>
              <span className="crm-composer-template-sub">Ventana 24h cerrada — solo se pueden mandar templates aprobados</span>
            </span>
            <span className="crm-composer-template-arrow">→</span>
          </button>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,audio/mpeg,audio/mp3,audio/ogg,audio/aac,audio/mp4,audio/amr,video/mp4,video/3gpp"
              style={{ display: 'none' }}
              onChange={handleAdjuntar}
            />
            <button
              className="crm-composer-attach"
              onClick={() => fileInputRef.current?.click()}
              disabled={subiendoAdjunto || enviando}
              title={subiendoAdjunto ? 'Subiendo...' : 'Adjuntar (img/PDF/audio/video, máx 16 MB)'}
            >{subiendoAdjunto ? '⋯' : '📎'}</button>
            <div className="crm-composer-box">
              {slashOpen && respuestasFiltradas.length > 0 && (
                <div className="crm-slash-dropdown">
                  <div className="crm-slash-head">
                    <span>💬 Respuestas rápidas</span>
                    <span className="crm-slash-hint">↑↓ navegar · Enter aplicar · Esc cerrar</span>
                  </div>
                  {respuestasFiltradas.map((r, i) => (
                    <button
                      key={r.id}
                      className={`crm-slash-item ${i === slashIndex ? 'is-active' : ''}`}
                      onMouseEnter={() => setSlashIndex(i)}
                      onClick={() => aplicarRespuesta(r)}
                    >
                      <div className="crm-slash-item-head">
                        <span className="crm-slash-slug">/{r.slug}</span>
                        <span className="crm-slash-titulo">{r.titulo}</span>
                        {r.categoria && <span className={`crm-slash-cat crm-slash-cat-${r.categoria}`}>{r.categoria}</span>}
                      </div>
                      <div className="crm-slash-preview">{r.texto.slice(0, 90)}{r.texto.length > 90 ? '…' : ''}</div>
                    </button>
                  ))}
                </div>
              )}
              <textarea
                ref={inputRef}
                className="crm-composer-input"
                placeholder="Escribí tu respuesta… (Enter para enviar, / para respuestas rápidas, ⌘V para pegar imagen)"
                value={borrador}
                onChange={onBorradorChange}
                onKeyDown={onKeyDown}
                onPaste={handlePaste}
                rows={1}
              />
            </div>
            <button
              className="crm-composer-suggest"
              onClick={() => pedirSugerencia()}
              disabled={sugiriendo}
              title="Pedir sugerencia a Claude"
            >
              {sugiriendo ? '⋯' : '✨'}
            </button>
            <button
              className="crm-composer-send"
              onClick={handleEnviar}
              disabled={!borrador.trim() || enviando}
            >
              {enviando ? '…' : '➤'}
            </button>
          </>
        )}
      </footer>

      {templateModalOpen && (
        <NuevaConvModal
          onClose={() => setTemplateModalOpen(false)}
          onCreated={(nuevoConvId) => {
            setTemplateModalOpen(false)
            // Si la conv resultante es la misma que ya estamos viendo, recargamos para ver el mensaje template enviado.
            // Si es otra (raro, ej. el usuario cambió de cliente desde el modal), saltamos a ella.
            if (nuevoConvId && nuevoConvId !== conversationId && onSeleccionar) onSeleccionar(nuevoConvId)
            else cargarTodo()
          }}
          preset={{
            phone: conv?.phone || '',
            nombre: conv?.nombre_visible || '',
            clienteId: conv?.cliente_id || null,
          }}
        />
      )}

      {adjuntoPreview && (
        <PreviewAdjunto
          adj={adjuntoPreview}
          subiendo={subiendoAdjunto}
          onCancelar={cerrarPreviewAdjunto}
          onEnviar={confirmarEnvioAdjunto}
        />
      )}
    </>
  )
}

function PreviewAdjunto({ adj, subiendo, onCancelar, onEnviar }) {
  const [caption, setCaption] = useState(adj.captionDraft || '')

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onEnviar(caption)
    }
    if (e.key === 'Escape') onCancelar()
  }

  return (
    <div className="crm-preview-bg" onClick={onCancelar}>
      <div className="crm-preview-modal" onClick={(e) => e.stopPropagation()}>
        <button className="crm-preview-close" onClick={onCancelar} title="Cancelar (Esc)">×</button>

        <div className="crm-preview-media">
          {adj.tipoWA === 'image' && adj.previewUrl && (
            <img src={adj.previewUrl} alt={adj.filename} />
          )}
          {adj.tipoWA === 'video' && adj.previewUrl && (
            <video src={adj.previewUrl} controls />
          )}
          {adj.tipoWA === 'audio' && (
            <div className="crm-preview-doc">
              <div className="crm-preview-doc-icon">🎤</div>
              <div className="crm-preview-doc-info">
                <div className="crm-preview-doc-name">{adj.filename}</div>
                <div className="crm-preview-doc-meta">{adj.sizeKb} KB · {adj.mime}</div>
              </div>
            </div>
          )}
          {adj.tipoWA === 'document' && (
            <div className="crm-preview-doc">
              <div className="crm-preview-doc-icon">📄</div>
              <div className="crm-preview-doc-info">
                <div className="crm-preview-doc-name">{adj.filename}</div>
                <div className="crm-preview-doc-meta">{adj.sizeKb} KB</div>
              </div>
            </div>
          )}
        </div>

        <div className="crm-preview-footer">
          <input
            type="text"
            className="crm-preview-caption"
            placeholder="Agregar un comentario… (opcional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            onKeyDown={onKeyDown}
            autoFocus
            disabled={subiendo}
          />
          <button className="crm-preview-btn-cancel" onClick={onCancelar} disabled={subiendo}>
            Cancelar
          </button>
          <button className="crm-preview-btn-send" onClick={() => onEnviar(caption)} disabled={subiendo}>
            {subiendo ? '⏳ Enviando…' : '➤ Enviar'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Burbuja({ m, conversationId, canal, onAvatarActualizado }) {
  const lado = m.direccion === 'in' ? 'in' : 'out'
  const sistema = m.direccion === 'out' && !m.agente_id
  const [pickerOpen, setPickerOpen] = useState(false)

  // Si es un mensaje automatizado (trigger DB o cron disparó wa-send), lo pinto distinto
  // para que admin/superadmin diferencien de un msg humano.
  if (m.origen === 'auto' || m.automatizacion_id) {
    return (
      <div style={{ padding: '4px 12px' }}>
        <AutoBubbleShared msg={m} />
      </div>
    )
  }

  async function ponerComoAvatar(e) {
    e?.preventDefault(); e?.stopPropagation()
    if (!m.media_url) return
    try {
      await usarMensajeComoAvatar(conversationId, m.media_url)
      onAvatarActualizado?.(m.media_url)
    } catch (err) { alert('Error: ' + err.message) }
  }

  async function reaccionar(emoji) {
    setPickerOpen(false)
    try {
      // Si ya reaccionó con el mismo emoji → quitar (toggle)
      if (m.reaction && m.reaction_from === 'agente' && reactionEmoji(m.reaction) === emoji) {
        await quitarReaccionMensaje({ conversationId, canal, messageId: m.id })
      } else {
        await reaccionarMensaje({ conversationId, canal, messageId: m.id, emoji })
      }
    } catch (err) { alert('No se pudo reaccionar: ' + err.message) }
  }

  if (m.tipo === 'system') {
    return <div className="crm-msg-sys">{m.texto}</div>
  }

  return (
    <div className={`crm-msg-wrap crm-msg-${lado}`}>
      <div className={`crm-msg ${sistema ? 'crm-msg-sistema' : ''}`}>
        {!sistema && lado === 'in' && (
          <div className="crm-msg-react-trigger">
            <button
              className="crm-msg-react-btn"
              onClick={() => setPickerOpen(p => !p)}
              title="Reaccionar"
            >
              {m.reaction && m.reaction_from === 'agente' ? reactionEmoji(m.reaction) : '😊+'}
            </button>
            {pickerOpen && (
              <div className="crm-react-picker" onMouseLeave={() => setPickerOpen(false)}>
                {['❤️','👍','😂','😮','😢','🔥'].map(e => (
                  <button key={e} className="crm-react-pick" onClick={() => reaccionar(e)}>{e}</button>
                ))}
              </div>
            )}
          </div>
        )}
        {sistema && <div className="crm-msg-sys-tag">📨 Sistema</div>}
        {m.tipo === 'image' && m.media_url && (
          <div className="crm-msg-imgcontainer">
            <a href={m.media_url} target="_blank" rel="noreferrer" className="crm-msg-imgwrap">
              <img src={m.media_url} alt={m.media_filename || 'Foto'} className="crm-msg-img" />
            </a>
            {lado === 'in' && (
              <button
                className="crm-msg-usaravatar"
                onClick={ponerComoAvatar}
                title="Usar esta foto como avatar del cliente"
              >
                📷 Usar como avatar
              </button>
            )}
          </div>
        )}
        {m.tipo === 'image' && !m.media_url && (
          <div className="crm-msg-media">📷 {m.media_filename || 'Foto'}</div>
        )}
        {m.tipo === 'document' && (
          m.media_url ? (
            <a href={m.media_url} target="_blank" rel="noreferrer" className="crm-msg-media crm-msg-media-link">
              📄 {m.media_filename || 'Documento'}
            </a>
          ) : (
            <div className="crm-msg-media">📄 {m.media_filename || 'Documento'}</div>
          )
        )}
        {m.tipo === 'audio' && (
          m.media_url ? (
            <audio controls src={m.media_url} className="crm-msg-audio" />
          ) : <div className="crm-msg-media">🎤 Audio</div>
        )}
        {m.tipo === 'video' && (
          m.media_url ? (
            <video controls src={m.media_url} className="crm-msg-video" />
          ) : <div className="crm-msg-media">🎥 Video</div>
        )}
        {m.tipo === 'template' && (
          <div className="crm-msg-tmpl">📨 Plantilla: <b>{m.template_name}</b></div>
        )}
        {m.texto && <div className="crm-msg-texto">{m.texto}</div>}
        {m.reaction && (
          <div className="crm-msg-reaction" title={`${m.reaction_from === 'cliente' ? 'Cliente' : 'Agente'} reaccionó: ${m.reaction}`}>
            {reactionEmoji(m.reaction)}
          </div>
        )}
      </div>
      <div className="crm-msg-meta">
        {fmtTiempoCorto(m.created_at)}
        {m.direccion === 'out' && m.estado && (
          <> · {tickEstado(m.estado)}</>
        )}
        {m.agente?.nombre && <> · {m.agente.nombre}</>}
      </div>
    </div>
  )
}

// Mapea nombre de reacción de Meta IG (love, wow, etc) al emoji correspondiente
function reactionEmoji(r) {
  if (!r) return ''
  // Si ya es un emoji, devolverlo directo
  if (/[\u{1F300}-\u{1F9FF}\u{2600}-\u{27BF}\u{2764}\u{2728}]/u.test(r)) return r
  const map = {
    love: '❤️', heart: '❤️', like: '👍',
    wow: '😮', surprised: '😮',
    laugh: '😂', haha: '😂',
    sad: '😢', cry: '😢',
    angry: '😠',
    fire: '🔥',
    thumbs_up: '👍', thumbs_down: '👎',
    other: '⭐',
  }
  return map[String(r).toLowerCase()] || '⭐'
}

function FichaPanel({ conversationId }) {
  const [conv, setConv] = useState(null)
  const [ficha, setFicha] = useState(null)
  const [timeline, setTimeline] = useState([])
  const [vincularOpen, setVincularOpen] = useState(false)
  const [contacto, setContacto] = useState(null)
  const [comprasSueltas, setComprasSueltas] = useState([])
  const navigate = useNavigate()

  async function recargar() {
    const list = await getConversaciones()
    const c = list.find(x => x.id === conversationId)
    setConv(c || null)
    if (c?.cliente_id) {
      const [f, tl] = await Promise.all([
        getFichaCliente(c.cliente_id),
        getTimelineCliente(c.cliente_id),
      ])
      setFicha(f)
      setTimeline(tl)
      // Compras sueltas: entregas de tipo 'unica' (no suscripción)
      // Fix 7-jul-2026 (auditoría): enum entrega_tipo no tiene 'unica',
      // los reales son 'compra_unica' y 'productos_sueltos'. La sección
      // "Compras sueltas" del sidebar CRM venía vacía siempre.
      const { data: cs } = await supabase
        .from('entregas')
        .select('id, fecha_programada, estado, monto_total, kit_codigo, tipo')
        .eq('cliente_id', c.cliente_id)
        .in('tipo', ['compra_unica', 'productos_sueltos'])
        .order('fecha_programada', { ascending: false })
        .limit(8)
      setComprasSueltas(cs || [])
    } else {
      setFicha(null)
      setTimeline([])
      setComprasSueltas([])
    }
    // Cargar contacto en paralelo (puede ser null si la conv recién se creó)
    const ct = await getContactoDeConversacion(conversationId)
    setContacto(ct)
  }

  useEffect(() => { recargar() }, [conversationId])

  async function handleVincular(clienteId) {
    try {
      await vincularConversacionACliente(conversationId, clienteId)
      setVincularOpen(false)
      await recargar()
    } catch (e) {
      alert(e.message)
    }
  }

  async function handleDesvincular() {
    if (!confirm('¿Desvincular esta conversación del cliente? Va a quedar como visitante.')) return
    try {
      await desvincularConversacionDeCliente(conversationId)
      await recargar()
    } catch (e) {
      alert(e.message)
    }
  }

  if (!conv) return <div className="crm-cliente-empty">Cargando…</div>

  const nombre = conv.nombre_visible || ficha?.titular?.nombre || (conv.phone ? '+' + conv.phone : 'Sin nombre')

  if (!conv.cliente_id) {
    // Subtítulo según canal (resolvemos también el bug del badge de cámara que era IG fijo)
    const sub = conv.canal === 'instagram'
      ? (conv.ig_username ? '@' + conv.ig_username : 'Instagram')
      : (conv.phone ? '+' + conv.phone : 'WhatsApp')
    return (
      <div className="crm-cliente-content">
        <div className="crm-cliente-hero">
          <AvatarFichaEditable
            conv={conv}
            nombre={nombre}
            onActualizar={(url) => setConv({ ...conv, foto_perfil_url: url, foto_perfil_source: url ? 'manual' : 'auto' })}
          />
          <div className="crm-cliente-h">{nombre}</div>
          <div className="crm-cliente-sub">{sub}</div>
          <ContactoTiposPills contacto={contacto} canal={conv.canal} />
        </div>

        <ContactoEditor
          contacto={contacto}
          onChange={(c) => setContacto(c)}
        />

        <div className="crm-cliente-actions">
          <button className="crm-cliente-btn crm-cliente-btn-primary" onClick={() => setVincularOpen(true)}>
            <span>🔗</span> Vincular a cliente Limpack
          </button>
          <Link to="/cliente/nueva-suscripcion" className="crm-cliente-btn">
            <span>✨</span> Crear suscripción nueva
          </Link>
          <button className="crm-cliente-btn" onClick={() => navigate('/admin/consultas-comerciales')}>
            <span>📥</span> Anotar en consultas
          </button>
        </div>

        {vincularOpen && (
          <VincularClienteModal
            onClose={() => setVincularOpen(false)}
            onVincular={handleVincular}
          />
        )}
      </div>
    )
  }

  return (
    <div className="crm-cliente-content">
      <div className="crm-cliente-hero">
        <AvatarFichaEditable
          conv={conv}
          nombre={nombre}
          onActualizar={(url) => setConv({ ...conv, foto_perfil_url: url, foto_perfil_source: url ? 'manual' : 'auto' })}
        />
        <div className="crm-cliente-h">{nombre}</div>
        <div className="crm-cliente-sub">📍 {ficha?.barrio || '—'}{ficha?.direccion ? ' · ' + ficha.direccion : ''}</div>
        <div className="crm-cliente-badges">
          <span className={`crm-cliente-badge ${ficha?.estado === 'activa' ? 'crm-cliente-badge-ok' : 'crm-cliente-badge-warn'}`}>
            {ficha?.estado || '—'}
          </span>
          {ficha?.kit?.tier && (
            <span className="crm-cliente-badge crm-cliente-badge-info">{ficha.kit.tier}</span>
          )}
        </div>
        <ContactoTiposPills contacto={contacto} canal={conv.canal} compact />
      </div>

      {/* KPIs en chips */}
      <KpiChips kpis={ficha?.kpis} />

      {ficha?.kit?.nombre ? (
        <>
          <div className="crm-cliente-label">Su suscripción</div>
          <div className="crm-cliente-box">
            <div className="crm-cliente-row"><span>Kit</span><strong>{ficha.kit.nombre}</strong></div>
            <div className="crm-cliente-row"><span>Cuota</span><strong>${ficha.kit.precio_susc?.toLocaleString('es-UY') || '—'}/mes</strong></div>
            <div className="crm-cliente-row">
              <span>Próx. cobro</span>
              <strong>
                {fmtFecha(ficha?.fecha_proximo_cobro)}
                <ProximoCobroPill dias={ficha?.kpis?.dias_hasta_proximo_cobro} />
              </strong>
            </div>
            <div className="crm-cliente-row"><span>Tarjeta</span><strong>{ficha?.mp_card_brand || '—'} •••{ficha?.mp_card_last4 || '----'}</strong></div>
          </div>

          {/* Último pago + próxima entrega real (datos enriquecidos) */}
          <UltimoPagoYProximaEntrega kpis={ficha?.kpis} />
        </>
      ) : (
        <>
          <div className="crm-cliente-label">Suscripción</div>
          <div className="crm-cliente-box" style={{ display:'flex', flexDirection:'column', gap: 6, padding: 14, background: '#FFF7ED', border: '0.5px solid #FCD9B6' }}>
            <div style={{ display:'flex', alignItems:'center', gap: 6, fontSize: 13, color: '#A16207', fontWeight: 600 }}>
              <span>⚠</span> No tiene suscripción activa
            </div>
            <div style={{ fontSize: 12, color: '#78716C' }}>
              Este cliente no tiene plan mensual. {comprasSueltas.length > 0 ? `Hizo ${comprasSueltas.length} compra(s) suelta(s) hasta ahora.` : 'Tampoco hay compras sueltas.'}
            </div>
            <Link to="/cliente/nueva-suscripcion" className="crm-cliente-btn" style={{ marginTop: 6, textAlign:'center' }}>
              ✨ Ofrecer suscripción
            </Link>
          </div>

          {comprasSueltas.length > 0 && (
            <>
              <div className="crm-cliente-label">Compras sueltas ({comprasSueltas.length})</div>
              <div className="crm-cliente-box" style={{ padding: 0 }}>
                {comprasSueltas.map(c => (
                  <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 12px', borderBottom: '0.5px solid var(--c-line)', fontSize: 12.5 }}>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--c-ink)' }}>{c.kit_codigo || 'Compra suelta'}</div>
                      <div style={{ fontSize: 11, color: 'var(--c-muted)' }}>
                        {c.fecha_programada ? new Date(c.fecha_programada + 'T12:00').toLocaleDateString('es-UY', { day:'numeric', month:'short', year:'numeric' }) : '—'}
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontWeight: 600 }}>${(c.monto_total || 0).toLocaleString('es-UY')}</div>
                      <div style={{ fontSize: 10.5, color: c.estado === 'entregada' ? '#16A34A' : c.estado === 'no_entregada' ? '#DC2626' : '#D97706', fontWeight: 600 }}>{c.estado}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <div className="crm-cliente-actions">
        <button className="crm-cliente-btn" onClick={() => navigate(`/admin/clientes?q=${ficha?.id}`)}>
          <span>📦</span> Ver suscripción completa
        </button>
        <button className="crm-cliente-btn" onClick={() => navigate(`/admin/cobros?cliente=${ficha?.id}`)}>
          <span>💳</span> Historial de pagos
        </button>
        <button className="crm-cliente-btn" onClick={() => navigate(`/admin/entregas?cliente=${ficha?.id}`)}>
          <span>🚚</span> Entregas
        </button>
      </div>

      {/* Meta de la conversación: agente, estado, tags, notas */}
      <ConversacionMeta conv={conv} />

      <TimelineConFiltros eventos={timeline} />

      <div className="crm-cliente-desvincular-wrap">
        <button className="crm-cliente-desvincular-btn" onClick={handleDesvincular} title="Desvincular esta conversación del cliente">
          Desvincular del cliente
        </button>
      </div>
    </div>
  )
}

/**
 * Modal para vincular una conversación huérfana con un cliente existente.
 * Buscador + lista de clientes, al elegir uno se llama onVincular(clienteId).
 */
function VincularClienteModal({ onClose, onVincular }) {
  const [clientes, setClientes] = useState([])
  const [busca, setBusca] = useState('')
  const [cargando, setCargando] = useState(true)
  const [sel, setSel] = useState(null)
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    setCargando(true)
    buscarClientesParaCRM('').then(c => { setClientes(c); setCargando(false) })
  }, [])

  useEffect(() => {
    const t = setTimeout(() => {
      buscarClientesParaCRM(busca).then(setClientes)
    }, 200)
    return () => clearTimeout(t)
  }, [busca])

  async function confirmar() {
    if (!sel) return
    setEnviando(true)
    try {
      await onVincular(sel.id)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="crm-modal-head">
          <h2>Vincular con cliente</h2>
          <button className="crm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="crm-modal-body">
          <p className="crm-modal-hint">
            Buscá al cliente existente al que pertenece esta conversación. Va a quedar asociada y vas a ver su ficha completa acá.
          </p>
          <input
            className="crm-modal-input"
            placeholder="Buscar por alias, nombre o teléfono…"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            autoFocus
          />
          <div className="crm-modal-list">
            {cargando ? (
              <div className="crm-modal-empty">Cargando…</div>
            ) : clientes.length === 0 ? (
              <div className="crm-modal-empty">No se encontraron clientes</div>
            ) : clientes.slice(0, 12).map(c => (
              <button
                key={c.id}
                className={`crm-modal-item ${sel?.id === c.id ? 'is-selected' : ''}`}
                onClick={() => setSel(c)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{c.alias || c.titular?.nombre || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--c-muted)' }}>
                    {c.titular?.nombre && c.alias && c.titular.nombre !== c.alias ? `${c.titular.nombre} · ` : ''}
                    {c.titular?.phone ? `+${c.titular.phone}` : 'sin teléfono'}
                    {c.barrio ? ` · ${c.barrio}` : ''}
                  </div>
                </div>
                <span className={`crm-cliente-badge ${c.estado === 'activa' ? 'crm-cliente-badge-ok' : 'crm-cliente-badge-warn'}`}>
                  {c.estado || '—'}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="crm-modal-foot">
          <button className="crm-modal-btn crm-modal-btn-secondary" onClick={onClose}>Cancelar</button>
          <button
            className="crm-modal-btn crm-modal-btn-primary"
            disabled={!sel || enviando}
            onClick={confirmar}
          >
            {enviando ? 'Vinculando…' : 'Vincular'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ConversacionMeta({ conv }) {
  if (!conv) return null
  const tieneInfo = conv.agente?.nombre || conv.tags?.length > 0 || conv.notas_internas
  if (!tieneInfo) return null

  return (
    <>
      <div className="crm-cliente-label">Esta conversación</div>
      <div className="crm-cliente-box crm-meta-conv">
        {conv.agente?.nombre && (
          <div className="crm-cliente-row">
            <span>Asignada a</span>
            <strong className="crm-meta-assignee">
              <span className="crm-assignee-avatar">{iniciales(conv.agente.nombre)}</span>
              {conv.agente.nombre}
            </strong>
          </div>
        )}
        {conv.tags?.length > 0 && (
          <div className="crm-cliente-row" style={{ alignItems: 'flex-start' }}>
            <span>Etiquetas</span>
            <div className="crm-meta-tags">
              {conv.tags.map(t => <span key={t} className="crm-tag-chip-static">#{t}</span>)}
            </div>
          </div>
        )}
        {conv.notas_internas && (
          <div className="crm-meta-notas">
            <div className="crm-meta-notas-label">📋 Notas internas</div>
            <div className="crm-meta-notas-texto">{conv.notas_internas}</div>
          </div>
        )}
      </div>
    </>
  )
}

const FILTROS_TIMELINE = [
  { key: 'todos', label: 'Todo', tipos: null },
  { key: 'mensajes', label: 'Mensajes', tipos: ['mensaje_in','mensaje_out'] },
  { key: 'cobros', label: 'Cobros', tipos: ['cobro_ok','cobro_fallido'] },
  { key: 'entregas', label: 'Entregas', tipos: ['entrega_programada','entrega_ok','entrega_no_entregada'] },
  { key: 'cambios', label: 'Cambios', tipos: ['suscripcion_alta','suscripcion_cambio','pausa','cancelacion','asignacion_cambio','estado_cambio','tag_cambio'] },
]

function TimelineConFiltros({ eventos }) {
  const [filtro, setFiltro] = useState('todos')
  const cur = FILTROS_TIMELINE.find(f => f.key === filtro) || FILTROS_TIMELINE[0]
  const filtrados = cur.tipos ? eventos.filter(e => cur.tipos.includes(e.tipo)) : eventos

  // Contadores por filtro
  const counts = FILTROS_TIMELINE.reduce((acc, f) => {
    acc[f.key] = f.tipos ? eventos.filter(e => f.tipos.includes(e.tipo)).length : eventos.length
    return acc
  }, {})

  return (
    <>
      <div className="crm-cliente-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Timeline 360</span>
        <span style={{ fontSize: 10, color: 'var(--c-muted)' }}>{eventos.length} eventos</span>
      </div>
      <div className="crm-timeline-filtros">
        {FILTROS_TIMELINE.map(f => (
          <button
            key={f.key}
            className={`crm-timeline-filtro ${filtro === f.key ? 'is-active' : ''}`}
            onClick={() => setFiltro(f.key)}
            disabled={counts[f.key] === 0}
          >
            {f.label} <span className="crm-timeline-filtro-count">{counts[f.key]}</span>
          </button>
        ))}
      </div>
      <div className="crm-timeline">
        {filtrados.length === 0 ? (
          <div className="crm-timeline-empty">
            {eventos.length === 0
              ? 'Sin eventos todavía. Cuando llegue un mensaje, se programen entregas o haya cobros, aparecen acá.'
              : 'Sin eventos de este tipo en el timeline.'}
          </div>
        ) : (
          filtrados.map(ev => <EventoTimeline key={ev.id} ev={ev} />)
        )}
      </div>
    </>
  )
}

function EventoTimeline({ ev }) {
  const icono = {
    mensaje_in: '💬',
    mensaje_out: '↗',
    suscripcion_alta: '✨',
    suscripcion_cambio: '🔁',
    cobro_ok: '✅',
    cobro_fallido: '❌',
    entrega_programada: '📅',
    entrega_ok: '📦',
    entrega_no_entregada: '⚠️',
    pausa: '⏸',
    cancelacion: '🚫',
    login: '🔐',
    vista_landing: '👀',
    asignacion_cambio: '👤',
    estado_cambio: '🔀',
    tag_cambio: '🏷',
  }[ev.tipo] || '•'

  const colorBorde = {
    3: '#DC2626',
    2: '#D97706',
    1: '#1A3461',
    0: '#94A3B8',
  }[ev.importancia] || '#94A3B8'

  return (
    <div className="crm-timeline-item" style={{ borderLeftColor: colorBorde }}>
      <div className="crm-timeline-icon">{icono}</div>
      <div className="crm-timeline-body">
        <div className="crm-timeline-titulo">{ev.titulo}</div>
        {ev.descripcion && <div className="crm-timeline-desc">{ev.descripcion}</div>}
        <div className="crm-timeline-fecha">{fmtFechaLarga(ev.created_at)}</div>
      </div>
    </div>
  )
}

// Avatar de la ficha (88px) con hover overlay para subir foto manual o quitar.
function AvatarFichaEditable({ conv, nombre, onActualizar }) {
  const fileRef = useRef(null)
  const [subiendo, setSubiendo] = useState(false)

  async function onPick(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setSubiendo(true)
    try {
      const url = await subirAvatarConversacion(file, conv.id)
      onActualizar?.(url)
    } catch (err) {
      alert('Error subiendo foto: ' + err.message)
    } finally {
      setSubiendo(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function onQuitar() {
    if (!confirm('¿Quitar la foto? Vuelve al avatar generativo único.')) return
    try {
      await quitarAvatar(conv.id)
      onActualizar?.(null)
    } catch (err) { alert(err.message) }
  }

  return (
    <div className="crm-cliente-hero-avatar-wrap">
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPick} />
      <Avatar
        foto={conv.foto_perfil_url}
        seed={conv.phone || conv.ig_username || conv.id}
        size={88}
      />
      {/* Badge canal (esquina superior derecha) — claro para que no se confunda con el botón cámara */}
      <div className="crm-cliente-canal-badge" title={canalLabel(conv.canal)}>
        <CanalLogo canal={conv.canal} size={18} />
      </div>
      <div className="crm-cliente-hero-avatar-actions">
        <button
          className="crm-cliente-avatar-btn"
          onClick={() => fileRef.current?.click()}
          disabled={subiendo}
          title={conv.foto_perfil_url ? 'Cambiar foto' : 'Subir foto'}
        >
          {subiendo ? '⋯' : '📷'}
        </button>
        {conv.foto_perfil_url && (
          <button
            className="crm-cliente-avatar-btn crm-cliente-avatar-btn-x"
            onClick={onQuitar}
            title="Quitar foto"
          >×</button>
        )}
      </div>
    </div>
  )
}

// Pills con los tipos del contacto (cliente / proveedor / interno / prospecto / otro)
// Si compact=true se muestra inline chiquito. Sin onChange aquí — el editor está abajo.
function ContactoTiposPills({ contacto, canal, compact = false }) {
  if (!contacto || !contacto.tipos || contacto.tipos.length === 0) {
    return (
      <div className="crm-cliente-badges">
        <span className="crm-cliente-badge crm-cliente-badge-warn">Sin clasificar</span>
      </div>
    )
  }
  return (
    <div className="crm-cliente-badges">
      {contacto.tipos.map(t => {
        const meta = TIPOS_CONTACTO.find(x => x.v === t)
        if (!meta) return null
        return (
          <span
            key={t}
            className="crm-cliente-badge"
            style={{ background: meta.color + '20', color: meta.color, borderColor: meta.color + '40' }}
          >
            <span style={{ marginRight: 4 }}>{meta.icon}</span>{meta.l}
          </span>
        )
      })}
      {contacto.empresa && (
        <span className="crm-cliente-badge crm-cliente-badge-info">
          🏢 {contacto.empresa}
        </span>
      )}
    </div>
  )
}

// Editor inline de contacto: nombre/email + tipos + empresa + notas.
function ContactoEditor({ contacto, onChange }) {
  const [nombre, setNombre] = useState(contacto?.nombre || '')
  const [email, setEmail] = useState(contacto?.email || '')
  const [empresa, setEmpresa] = useState(contacto?.empresa || '')
  const [notas, setNotas] = useState(contacto?.notas || '')
  const [guardando, setGuardando] = useState(false)
  const [hint, setHint] = useState(null)

  // Sincronizar cuando cambia el contacto
  useEffect(() => {
    setNombre(contacto?.nombre || '')
    setEmail(contacto?.email || '')
    setEmpresa(contacto?.empresa || '')
    setNotas(contacto?.notas || '')
  }, [contacto?.id])

  if (!contacto) {
    return (
      <div className="crm-cliente-box" style={{ padding: 12, fontSize: 13, color: '#64748B' }}>
        Cargando ficha de contacto…
      </div>
    )
  }

  async function handleToggle(tipo) {
    setGuardando(true)
    try {
      const actualizado = await toggleTipoContacto(contacto.id, tipo)
      onChange?.(actualizado)
      setHint({ ok: true, txt: 'Tipo actualizado' })
    } catch (e) {
      setHint({ ok: false, txt: e.message })
    } finally {
      setGuardando(false)
      setTimeout(() => setHint(null), 2000)
    }
  }

  async function guardarCampo(campo, valor, original, label) {
    if (valor === (original || '')) return
    setGuardando(true)
    try {
      const actualizado = await actualizarContacto(contacto.id, { [campo]: valor || null })
      onChange?.(actualizado)
      setHint({ ok: true, txt: `${label} guardado` })
    } catch (e) {
      setHint({ ok: false, txt: e.message })
    } finally {
      setGuardando(false)
      setTimeout(() => setHint(null), 2000)
    }
  }
  const guardarNombre  = () => guardarCampo('nombre',  nombre.trim(),  contacto.nombre,  'Nombre')
  const guardarEmail   = () => guardarCampo('email',   email.trim(),   contacto.email,   'Email')
  const guardarEmpresa = () => guardarCampo('empresa', empresa.trim(), contacto.empresa, 'Empresa')

  const guardarNotas = () => guardarCampo('notas', notas.trim(), contacto.notas, 'Notas')

  const tieneAlguno = (contacto.tipos || []).length > 0

  return (
    <>
      <div className="crm-cliente-label">¿Quién es este contacto?</div>
      <div className="crm-cliente-box" style={{ padding: 12 }}>
        {/* Nombre + Email */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Nombre y apellido
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              onBlur={guardarNombre}
              placeholder="Lucía García"
              className="crm-contacto-input"
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={guardarEmail}
              placeholder="contacto@empresa.com"
              className="crm-contacto-input"
            />
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {TIPOS_CONTACTO.map(t => {
            const activo = (contacto.tipos || []).includes(t.v)
            return (
              <button
                key={t.v}
                onClick={() => handleToggle(t.v)}
                disabled={guardando}
                className="crm-contacto-tipo-btn"
                style={{
                  background: activo ? t.color : '#fff',
                  color: activo ? '#fff' : t.color,
                  borderColor: t.color,
                }}
                title={activo ? `Quitar ${t.l}` : `Marcar como ${t.l}`}
              >
                {t.icon} {t.l}
              </button>
            )
          })}
        </div>

        {(contacto.tipos || []).includes('proveedor') && (
          <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Empresa
            </label>
            <input
              type="text"
              value={empresa}
              onChange={(e) => setEmpresa(e.target.value)}
              onBlur={guardarEmpresa}
              placeholder="Ej: MercadoPago, Boyerco, NEBA"
              className="crm-contacto-input"
            />
          </div>
        )}

        <div style={{ marginTop: 8 }}>
          <label style={{ fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Notas internas
          </label>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            onBlur={guardarNotas}
            placeholder="Algo que querés recordar sobre este contacto…"
            className="crm-contacto-input"
            rows={2}
          />
        </div>

        {hint && (
          <div style={{ marginTop: 6, fontSize: 11, color: hint.ok ? '#16A34A' : '#DC2626' }}>
            {hint.ok ? '✓' : '✗'} {hint.txt}
          </div>
        )}

        {!tieneAlguno && (
          <div style={{ marginTop: 6, fontSize: 11, color: '#94A3B8' }}>
            Marcá uno o más tipos para clasificar a este contacto.
          </div>
        )}
      </div>
    </>
  )
}

function KpiChips({ kpis }) {
  if (!kpis) return null
  const meses = kpis.meses_como_cliente ?? 0
  const total = Number(kpis.total_pagado || 0)
  const entOk = kpis.entregas_ok ?? 0
  const entFail = kpis.entregas_fail ?? 0
  const pagFail = kpis.pagos_fallidos ?? 0

  return (
    <div className="crm-kpi-chips">
      <div className="crm-kpi-chip" title="Tiempo como cliente">
        <span className="crm-kpi-val">{meses}</span>
        <span className="crm-kpi-lbl">{meses === 1 ? 'mes' : 'meses'}</span>
      </div>
      <div className="crm-kpi-chip" title="Total pagado histórico">
        <span className="crm-kpi-val">${total.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</span>
        <span className="crm-kpi-lbl">pagado</span>
      </div>
      <div className="crm-kpi-chip" title={`${entOk} OK · ${entFail} fallidas`}>
        <span className="crm-kpi-val">{entOk}</span>
        <span className="crm-kpi-lbl">entregas</span>
      </div>
      {pagFail > 0 && (
        <div className="crm-kpi-chip is-warn" title="Pagos fallidos históricos">
          <span className="crm-kpi-val">{pagFail}</span>
          <span className="crm-kpi-lbl">cobros fallidos</span>
        </div>
      )}
    </div>
  )
}

function ProximoCobroPill({ dias }) {
  if (dias === null || dias === undefined) return null
  if (dias < 0) {
    return <span className="crm-cobro-pill is-vencido" title="Cobro vencido"> · vencido hace {Math.abs(dias)}d</span>
  }
  if (dias === 0) {
    return <span className="crm-cobro-pill is-hoy" title="Cobro hoy"> · hoy</span>
  }
  if (dias <= 3) {
    return <span className="crm-cobro-pill is-cerca" title="Cobro inminente"> · en {dias}d</span>
  }
  return <span style={{ color: 'var(--c-muted-2)', fontWeight: 400, fontSize: 11 }}> · en {dias}d</span>
}

function UltimoPagoYProximaEntrega({ kpis }) {
  if (!kpis) return null
  const up = kpis.ultimo_pago
  const pe = kpis.proxima_entrega
  if (!up && !pe) return null

  return (
    <>
      <div className="crm-cliente-label">Histórico</div>
      <div className="crm-cliente-box">
        {up && (
          <div className="crm-cliente-row" style={{ alignItems: 'flex-start' }}>
            <span>Último pago</span>
            <div style={{ textAlign: 'right' }}>
              <strong>${Number(up.monto || 0).toLocaleString('es-UY')}</strong>
              <div style={{ fontSize: 11, color: 'var(--c-muted)', marginTop: 2 }}>
                {fmtFecha(up.fecha)} · {up.metodo || '—'} ·{' '}
                <span className={`crm-pago-estado crm-pago-estado-${up.estado}`}>{up.estado}</span>
              </div>
            </div>
          </div>
        )}
        {pe && (
          <div className="crm-cliente-row" style={{ alignItems: 'flex-start' }}>
            <span>Próx. entrega</span>
            <div style={{ textAlign: 'right' }}>
              <strong>{fmtFecha(pe.fecha_programada)}</strong>
              <div style={{ fontSize: 11, color: 'var(--c-muted)', marginTop: 2 }}>
                {pe.numero_orden} · kit {pe.kit_codigo} · {pe.estado}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

function NuevaConvModal({ onClose, onCreated, preset = null }) {
  // Si viene preset (desde un chat con ventana cerrada), pre-cargamos tab + campos.
  const presetTieneCliente = !!preset?.clienteId
  const [tab, setTab] = useState(presetTieneCliente ? 'cliente' : (preset?.phone ? 'manual' : 'cliente'))
  const [templates, setTemplates] = useState([])
  const [tplSel, setTplSel] = useState(null)
  const [varValues, setVarValues] = useState([])
  const [clientes, setClientes] = useState([])
  const [busca, setBusca] = useState('')
  const [clienteSel, setClienteSel] = useState(null)
  const [phoneManual, setPhoneManual] = useState(preset?.phone || '')
  const [nombreManual, setNombreManual] = useState(preset?.nombre || '')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState(null)
  const [loadingTpls, setLoadingTpls] = useState(true)

  useEffect(() => {
    getTemplatesAprobados().then(t => { setTemplates(t); setLoadingTpls(false) })
    buscarClientesParaCRM('').then(list => {
      setClientes(list)
      // Si vino preset.clienteId, pre-seleccionar ese cliente
      if (presetTieneCliente) {
        const found = list.find(c => c.id === preset.clienteId)
        if (found) setClienteSel(found)
      }
    })
  }, [])

  // Buscar clientes con debounce simple
  useEffect(() => {
    if (tab !== 'cliente') return
    const t = setTimeout(() => {
      buscarClientesParaCRM(busca).then(setClientes)
    }, 250)
    return () => clearTimeout(t)
  }, [busca, tab])

  function seleccionarTemplate(t) {
    setTplSel(t)
    setVarValues(Array(t.variables_count).fill(''))
  }

  const preview = tplSel ? aplicarTemplateVars(tplSel.body, varValues) : ''
  const todasVarsLlenas = !tplSel || varValues.every(v => v && v.trim().length > 0)

  const destinoOK = tab === 'cliente'
    ? !!clienteSel?.titular?.phone
    : phoneManual.replace(/[^0-9]/g, '').length >= 9

  async function enviar() {
    setError(null)
    if (!tplSel) { setError('Elegí un template'); return }
    if (!destinoOK) { setError('Falta destino válido'); return }
    if (!todasVarsLlenas) { setError('Llená todas las variables'); return }
    setEnviando(true)
    try {
      const r = await iniciarConversacionConTemplate({
        phone: tab === 'cliente' ? clienteSel.titular.phone : phoneManual,
        nombreVisible: tab === 'cliente' ? (clienteSel.alias || clienteSel.titular.nombre) : (nombreManual || null),
        clienteId: tab === 'cliente' ? clienteSel.id : null,
        templateName: tplSel.name,
        templateLang: tplSel.language,
        templateParams: varValues,
      })
      if (onCreated) onCreated(r.conversationId)
    } catch (e) {
      setError(e.message)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal-card" onClick={e => e.stopPropagation()}>
        <div className="crm-modal-head">
          <h2>Iniciar conversación</h2>
          <button className="crm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="crm-modal-body">
          <p className="crm-modal-hint">
            Como el cliente todavía no escribió, hay que arrancar con un template aprobado por Meta. Si te responde, se abre la ventana 24h y podés mandar texto libre.
          </p>

          {/* Tabs Cliente / Número manual */}
          <div className="crm-modal-tabs">
            <button className={`crm-modal-tab ${tab === 'cliente' ? 'is-active' : ''}`} onClick={() => setTab('cliente')}>
              👤 Cliente existente
            </button>
            <button className={`crm-modal-tab ${tab === 'manual' ? 'is-active' : ''}`} onClick={() => setTab('manual')}>
              ☎ Número nuevo
            </button>
          </div>

          {tab === 'cliente' ? (
            <div className="crm-modal-section">
              <input
                className="crm-modal-input"
                placeholder="Buscar por alias, nombre o teléfono…"
                value={busca}
                onChange={e => setBusca(e.target.value)}
              />
              <div className="crm-modal-list">
                {clientes.length === 0 ? (
                  <div className="crm-modal-empty">Sin clientes</div>
                ) : clientes.slice(0, 10).map(c => (
                  <button
                    key={c.id}
                    className={`crm-modal-item ${clienteSel?.id === c.id ? 'is-selected' : ''}`}
                    onClick={() => setClienteSel(c)}
                  >
                    <div style={{ flex: 1 }}>
                      <div className="crm-modal-item-name">{c.alias || c.titular?.nombre || '—'}</div>
                      <div className="crm-modal-item-sub">
                        {c.titular?.phone ? '+' + c.titular.phone : '(sin teléfono)'} · {c.barrio || '—'} · {c.kit_actual || 'sin kit'}
                      </div>
                    </div>
                    {clienteSel?.id === c.id && <span style={{ color: 'var(--c-teal)' }}>✓</span>}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="crm-modal-section">
              <label className="crm-modal-label">Teléfono (formato Uruguay)</label>
              <input
                className="crm-modal-input"
                placeholder="098610001"
                value={phoneManual}
                onChange={e => setPhoneManual(e.target.value)}
              />
              <label className="crm-modal-label" style={{ marginTop: 10 }}>Nombre visible (opcional)</label>
              <input
                className="crm-modal-input"
                placeholder="Ej: Ana Pérez"
                value={nombreManual}
                onChange={e => setNombreManual(e.target.value)}
              />
            </div>
          )}

          {/* Selector de template */}
          <div className="crm-modal-section">
            <label className="crm-modal-label">Template aprobado</label>
            {loadingTpls ? (
              <div className="crm-modal-empty">Cargando templates…</div>
            ) : templates.length === 0 ? (
              <div className="crm-modal-empty" style={{ background: '#FEF3C7', color: '#92400E' }}>
                ⚠ No hay templates APPROVED todavía. Esperá a que Meta apruebe los que subimos (5-30 min UTILITY, hasta 24h MARKETING).
              </div>
            ) : (
              <div className="crm-modal-tpls">
                {templates.map(t => (
                  <button
                    key={t.name}
                    className={`crm-modal-tpl ${tplSel?.name === t.name ? 'is-selected' : ''}`}
                    onClick={() => seleccionarTemplate(t)}
                  >
                    <div className="crm-modal-tpl-name">
                      {t.name}
                      <span className={`crm-modal-tpl-cat crm-modal-tpl-cat-${t.category?.toLowerCase()}`}>{t.category}</span>
                    </div>
                    <div className="crm-modal-tpl-body">{t.body.slice(0, 140)}{t.body.length > 140 ? '…' : ''}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Variables del template */}
          {tplSel && tplSel.variables_count > 0 && (
            <div className="crm-modal-section">
              <label className="crm-modal-label">Completar variables</label>
              {Array.from({ length: tplSel.variables_count }, (_, i) => (
                <input
                  key={i}
                  className="crm-modal-input"
                  style={{ marginBottom: 6 }}
                  placeholder={`Valor para {{${i + 1}}}`}
                  value={varValues[i] || ''}
                  onChange={e => {
                    const nuevo = [...varValues]
                    nuevo[i] = e.target.value
                    setVarValues(nuevo)
                  }}
                />
              ))}
            </div>
          )}

          {/* Preview */}
          {tplSel && (
            <div className="crm-modal-section">
              <label className="crm-modal-label">Vista previa</label>
              <div className="crm-modal-preview">{preview}</div>
            </div>
          )}

          {error && <div className="crm-modal-error">⚠ {error}</div>}
        </div>
        <div className="crm-modal-foot">
          <button className="crm-modal-btn-ghost" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button
            className="crm-modal-btn-primary"
            onClick={enviar}
            disabled={enviando || !tplSel || !destinoOK || !todasVarsLlenas}
          >
            {enviando ? 'Enviando…' : 'Enviar template →'}
          </button>
        </div>
      </div>
    </div>
  )
}

function PinnedBanner({ tags }) {
  const has = t => tags.includes(t)
  const config = has('vip')
    ? { emoji: '⭐', color: '#7C2D12', bg: '#FEF3C7', borde: '#F59E0B', texto: 'Cliente VIP — tratá con cariño extra' }
    : has('queja')
    ? { emoji: '⚠️', color: '#991B1B', bg: '#FEE2E2', borde: '#EF4444', texto: 'Conversación marcada como queja — escala si hace falta' }
    : (has('cancelación') || has('cancelada'))
    ? { emoji: '🚪', color: '#1E3A8A', bg: '#DBEAFE', borde: '#3B82F6', texto: 'Cliente cancelado o pidiendo cancelar — preguntale el motivo' }
    : has('cobro-vencido')
    ? { emoji: '⏰', color: '#92400E', bg: '#FEF3C7', borde: '#F59E0B', texto: 'Próximo cobro vencido — coordiná pago' }
    : { emoji: '💳', color: '#9A3412', bg: '#FFEDD5', borde: '#EA580C', texto: 'Cobro fallido reciente — coordiná nueva fecha' }

  return (
    <div className="crm-pinned" style={{ background: config.bg, borderLeftColor: config.borde, color: config.color }}>
      <span style={{ fontSize: 18 }}>{config.emoji}</span>
      <span>{config.texto}</span>
    </div>
  )
}

function SugerenciaCard({ sugerencia, sugiriendo, error, onUsar, onDescartar }) {
  if (sugiriendo && !sugerencia) {
    return (
      <div className="crm-sug crm-sug-loading">
        <span className="crm-sug-spinner" />
        <span>Claude está pensando una respuesta…</span>
      </div>
    )
  }
  if (error) {
    return (
      <div className="crm-sug crm-sug-error">
        <span>⚠ {error}</span>
        <button className="crm-sug-x" onClick={onDescartar}>×</button>
      </div>
    )
  }
  if (!sugerencia?.sugerencia) return null

  const conf = sugerencia.confianza ?? 0.5
  const confLabel = conf >= 0.8 ? 'alta' : conf >= 0.5 ? 'media' : 'baja'
  const confColor = conf >= 0.8 ? '#16A34A' : conf >= 0.5 ? '#D97706' : '#94A3B8'

  return (
    <div className="crm-sug">
      <div className="crm-sug-head">
        <span className="crm-sug-icon">✨</span>
        <span className="crm-sug-titulo">Claude sugiere</span>
        {sugerencia.etiqueta && <span className="crm-sug-tag">{sugerencia.etiqueta}</span>}
        <span className="crm-sug-conf" style={{ color: confColor, borderColor: confColor }}>
          confianza {confLabel}
        </span>
        <button className="crm-sug-x" onClick={onDescartar} title="Descartar">×</button>
      </div>
      <div className="crm-sug-texto">{sugerencia.sugerencia}</div>
      <div className="crm-sug-actions">
        <button className="crm-sug-btn-primary" onClick={onUsar}>
          Usar respuesta →
        </button>
        <span className="crm-sug-hint">después podés editarla antes de enviar</span>
      </div>
    </div>
  )
}

/* ─────────── Utils ─────────── */

function iniciales(nombre) {
  if (!nombre) return '?'
  const partes = nombre.split(/\s+/).filter(Boolean)
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase()
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase()
}

function canalLabel(canal) {
  if (canal === 'whatsapp') return 'WhatsApp'
  if (canal === 'instagram') return 'Instagram'
  if (canal === 'email') return 'Email'
  if (canal === 'webchat') return 'Webchat'
  return canal || ''
}

function fmtTiempoCorto(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  const ahora = new Date()
  const diffH = (ahora - d) / 36e5
  if (diffH < 24) return d.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })
  if (diffH < 48) return 'ayer'
  return d.toLocaleDateString('es-UY', { day: 'numeric', month: 'short' })
}

function fmtDia(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  const ahora = new Date()
  const isHoy = d.toDateString() === ahora.toDateString()
  const ayer = new Date(ahora); ayer.setDate(ayer.getDate() - 1)
  const isAyer = d.toDateString() === ayer.toDateString()
  if (isHoy) return 'Hoy'
  if (isAyer) return 'Ayer'
  return d.toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
}

function renderMensajesConDias(mensajes, conversationId, canal) {
  const out = []
  let diaAnterior = null
  for (const m of mensajes) {
    const dia = m.created_at ? new Date(m.created_at).toDateString() : null
    if (dia !== diaAnterior) {
      out.push(<div className="crm-msg-day-sep" key={'day-' + dia}>{fmtDia(m.created_at)}</div>)
      diaAnterior = dia
    }
    out.push(<Burbuja key={m.id} m={m} conversationId={conversationId} canal={canal} />)
  }
  return out
}

function fmtFecha(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('es-UY', { day: 'numeric', month: 'short' })
}

function fmtFechaLarga(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleString('es-UY', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function tickEstado(estado) {
  return { pending: '⏳', sent: '✓', delivered: '✓✓', read: '✓✓ leído', failed: '⚠ falló' }[estado] || ''
}

function estadoColor(estado) {
  return { activa: '#16A34A', pausada: '#D97706', cancelada: '#94A3B8' }[estado] || '#1A3461'
}

/* ─────────── Estilos ─────────── */

function Estilos() {
  return <style>{`
    /* ============ CRM v3 — basado en mockup aprobado ============ */

    /* Fix layout: liberar el CRM del padding del AdminLayout */
    body.crm-fullscreen-mode .al-main { padding: 0 !important; min-height: 0 !important; height: calc(100vh - 50px); overflow: hidden; }
    body.crm-fullscreen-mode .al-main > .al-page-title { display: none; }
    @media (min-width: 1024px) {
      body.crm-fullscreen-mode .al-main { height: 100vh; }
    }

    .crm-wrap {
      --c-navy: #0F1B36;
      --c-navy-2: #1A3461;
      --c-teal: #00A8B5;
      --c-teal-soft: #E0F7F9;
      --c-paper: #F8FAFC;
      --c-paper-2: #F1F5F9;
      --c-line: #E2E8F0;
      --c-line-soft: #F1F5F9;
      --c-ink: #1A3461;
      --c-muted: #475569;
      --c-muted-2: #94A3B8;
      --c-success: #16A34A;
      --c-warning: #D97706;
      --c-danger: #DC2626;
      --shadow-card: 0 1px 2px rgba(15, 27, 54, 0.04);
      --shadow-bubble: 0 1px 2px rgba(0,0,0,0.04);
      --shadow-focus: 0 0 0 3px rgba(0, 168, 181, 0.15);
      display: flex;
      flex-direction: column;
      height: 100%;
      background: var(--c-paper);
      overflow: hidden;
      font-feature-settings: "cv02", "cv03", "cv04", "cv11";
    }
    .crm-wrap * { -webkit-font-smoothing: antialiased; }

    /* ─────────── Topbar (header del CRM con stats inline) ─────────── */
    .crm-topbar {
      background: var(--c-navy);
      color: #fff;
      padding: 12px 24px;
      display: flex; align-items: center; gap: 24px;
      flex-shrink: 0;
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }
    .crm-topbar-title { display: flex; align-items: center; gap: 12px; }
    .crm-topbar-title h1 { font-size: 18px; font-weight: 800; letter-spacing: -0.015em; color: #fff; margin: 0; }
    .crm-topbar-tag {
      background: rgba(0,168,181,0.18);
      color: var(--c-teal);
      font-size: 10px; font-weight: 700;
      padding: 3px 10px;
      border-radius: 999px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .crm-topbar-stats { display: flex; gap: 22px; margin-left: auto; }
    .crm-stat {
      font-size: 12px;
      color: rgba(255,255,255,0.6);
      display: flex; align-items: center; gap: 6px;
      font-variant-numeric: tabular-nums;
    }
    .crm-stat strong {
      color: #fff;
      font-weight: 700;
      font-size: 14px;
      letter-spacing: -0.01em;
    }
    .crm-stat-dot {
      width: 7px; height: 7px;
      border-radius: 50%;
    }
    .crm-stat-dot-warn { background: var(--c-warning); box-shadow: 0 0 0 3px rgba(217,119,6,0.2); }
    .crm-stat-dot-ok { background: var(--c-success); box-shadow: 0 0 0 3px rgba(22,163,74,0.2); }

    /* ─────────── Body 3 columnas ─────────── */
    .crm-body {
      flex: 1;
      display: grid;
      grid-template-columns: 340px 1fr 340px;
      overflow: hidden;
      min-height: 0;
    }

    /* ─────────── Inbox ─────────── */
    .crm-inbox {
      background: #fff;
      border-right: 1px solid var(--c-line);
      display: flex; flex-direction: column;
      min-height: 0;
    }
    .crm-inbox-head {
      padding: 18px 18px 14px;
      border-bottom: 1px solid var(--c-line);
      display: flex; flex-direction: column;
      gap: 14px;
      background: #fff;
    }
    .crm-inbox-title {
      display: flex; align-items: center; gap: 10px;
      font-size: 17px; font-weight: 800;
      color: var(--c-ink);
      letter-spacing: -0.015em;
    }
    .crm-badge-pend {
      background: var(--c-warning);
      color: #fff;
      font-size: 10px; font-weight: 800;
      padding: 2px 8px;
      border-radius: 999px;
      letter-spacing: 0.02em;
    }
    .crm-search {
      width: 100%;
      padding: 9px 12px 9px 36px;
      border: 1px solid var(--c-line);
      border-radius: 10px;
      font-family: inherit;
      font-size: 13px;
      color: var(--c-ink);
      background: var(--c-paper) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E") no-repeat 12px center;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .crm-search:focus { outline: none; border-color: var(--c-teal); box-shadow: var(--shadow-focus); background-color: #fff; }
    .crm-search::placeholder { color: var(--c-muted-2); }

    /* Filtros — chips compactas, look moderno */
    .crm-filtros {
      display: flex; gap: 4px;
      overflow-x: auto;
      padding-bottom: 0;
      flex-wrap: nowrap;
      scrollbar-width: none;
    }
    .crm-filtros::-webkit-scrollbar { display: none; }
    .crm-filtro {
      background: var(--c-paper);
      border: 1px solid transparent;
      color: var(--c-muted-2);
      padding: 6px 11px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      white-space: nowrap;
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: all 0.15s ease;
      line-height: 1;
    }
    .crm-filtro:hover { background: var(--c-paper-2); color: var(--c-ink); }
    .crm-filtro.is-active {
      background: var(--c-navy);
      color: #fff;
      border-color: var(--c-navy);
      box-shadow: 0 2px 8px rgba(26, 52, 97, 0.25);
    }
    .crm-filtro-count {
      background: rgba(15, 31, 61, 0.08);
      color: var(--c-muted-2);
      font-size: 10.5px;
      font-weight: 800;
      padding: 1px 7px;
      border-radius: 999px;
      min-width: 18px;
      text-align: center;
      letter-spacing: 0;
    }
    .crm-filtro.is-active .crm-filtro-count {
      background: rgba(255,255,255,0.22);
      color: #fff;
    }
    .crm-pill-count {
      background: rgba(255,255,255,0.5); color: var(--c-muted);
      font-size: 10px; font-weight: 800;
      padding: 0 5px; margin-left: 3px;
      border-radius: 999px; min-width: 16px;
      display: inline-block; text-align: center;
    }
    .crm-pill {
      background: var(--c-paper);
      border: 1px solid transparent;
      color: var(--c-muted-2);
      padding: 5px 11px;
      border-radius: 999px;
      font-size: 11.5px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      white-space: nowrap;
      transition: all 0.15s ease;
      display: inline-flex; align-items: center;
      gap: 5px;
      line-height: 1;
    }
    .crm-pill:hover { background: var(--c-paper-2); color: var(--c-ink); }
    .crm-pill.is-active {
      background: var(--c-teal-soft);
      color: var(--c-teal);
      border-color: rgba(0,168,181,0.18);
    }
    .crm-pill.is-active .crm-pill-count { background: rgba(0,168,181,0.18); color: var(--c-teal); }
    .crm-pill-pend { background: #FEF3C7; color: #92400E; }
    .crm-pill-pend:hover { background: #FDE68A; color: #78350F; }
    .crm-pill-pend.is-active {
      background: var(--c-warning); color: #fff; border-color: var(--c-warning);
      box-shadow: 0 2px 8px rgba(217, 119, 6, 0.28);
    }
    .crm-pill-pend .crm-pill-count { background: rgba(146, 64, 14, 0.18); color: #92400E; }
    .crm-pill-pend.is-active .crm-pill-count { background: rgba(255,255,255,0.25); color: #fff; }

    .crm-inbox-list { flex: 1; overflow-y: auto; padding: 4px 0; }
    .crm-inbox-list::-webkit-scrollbar { width: 6px; }
    .crm-inbox-list::-webkit-scrollbar-thumb { background: var(--c-line); border-radius: 4px; }
    .crm-inbox-list::-webkit-scrollbar-thumb:hover { background: var(--c-muted-2); }

    .crm-empty { padding: 48px 18px; text-align: center; color: var(--c-muted); font-size: 13px; }
    .crm-empty-icon { font-size: 32px; margin-bottom: 10px; opacity: 0.5; }
    .crm-empty-sub { font-size: 12px; color: var(--c-muted-2); margin-top: 6px; line-height: 1.5; }

    .crm-inbox-stats {
      padding: 12px 18px;
      border-top: 1px solid var(--c-line);
      font-size: 11px; color: var(--c-muted);
      display: flex; gap: 8px; align-items: center;
      background: #fff;
    }
    .crm-sep { color: var(--c-line); }

    .crm-conv {
      width: 100%;
      background: transparent;
      border: 0; border-bottom: 1px solid var(--c-line-soft);
      padding: 14px 18px 14px 22px;
      display: flex; gap: 13px;
      cursor: pointer; text-align: left;
      font-family: inherit;
      position: relative;
      transition: background 0.15s ease;
      align-items: flex-start;
    }
    .crm-conv:hover { background: var(--c-paper); }
    .crm-conv.is-active {
      background: linear-gradient(90deg, var(--c-teal-soft) 0%, rgba(0,168,181,0.05) 100%);
    }
    .crm-conv.is-active::before {
      content: '';
      position: absolute;
      left: 0; top: 12px; bottom: 12px;
      width: 3px;
      background: var(--c-teal);
      border-radius: 0 3px 3px 0;
    }
    .crm-conv-avatar {
      width: 44px; height: 44px;
      flex-shrink: 0;
      position: relative;
      border-radius: 50%;
      margin-top: 2px;
    }
    .crm-conv-canal {
      position: absolute;
      bottom: -3px; right: -3px;
      font-size: 11px;
      background: #fff;
      width: 18px; height: 18px;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 1px 2px rgba(15, 27, 54, 0.12);
      line-height: 1;
    }
    .crm-conv-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .crm-conv-top { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
    .crm-conv-nombre {
      font-size: 14px; font-weight: 600;
      color: var(--c-ink);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      letter-spacing: -0.01em;
    }
    .crm-conv.is-unread .crm-conv-nombre { font-weight: 700; }
    .crm-conv-tiempo {
      font-size: 11px;
      color: var(--c-muted-2);
      flex-shrink: 0;
      font-variant-numeric: tabular-nums;
    }
    .crm-conv.is-unread .crm-conv-tiempo { color: var(--c-teal); font-weight: 700; }
    .crm-conv-sub {
      font-size: 11px; color: var(--c-muted-2);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .crm-conv-preview {
      font-size: 13px; color: var(--c-muted);
      margin-top: 3px;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      display: flex; align-items: center; gap: 6px;
      line-height: 1.4;
    }
    .crm-conv.is-unread .crm-conv-preview { color: var(--c-ink); font-weight: 500; }
    .crm-conv-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
    .crm-conv-tags { display: flex; gap: 4px; margin-top: 6px; flex-wrap: wrap; }
    .crm-conv-tag {
      background: var(--c-paper-2); color: var(--c-muted);
      font-size: 10px; font-weight: 600;
      padding: 2px 7px; border-radius: 999px;
      letter-spacing: 0.01em;
    }
    .crm-conv-unread {
      background: var(--c-teal); color: #fff;
      font-size: 11px; font-weight: 800;
      min-width: 20px; height: 20px;
      border-radius: 999px; padding: 0 6px;
      display: flex; align-items: center; justify-content: center;
      align-self: center;
      box-shadow: 0 1px 3px rgba(0, 168, 181, 0.3);
    }

    /* ─────────── Chat ─────────── */
    .crm-chat {
      display: flex; flex-direction: column;
      background: #fff;
      min-height: 0;
      position: relative;
    }
    .crm-chat-empty {
      flex: 1;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      color: var(--c-muted-2);
      text-align: center;
      padding: 40px;
      background: var(--c-paper);
    }
    .crm-chat-empty-icon { font-size: 56px; margin-bottom: 18px; opacity: 0.4; }
    .crm-chat-empty-h { font-size: 16px; font-weight: 700; color: var(--c-ink); margin-bottom: 6px; letter-spacing: -0.01em; }
    .crm-chat-empty-sub { font-size: 13px; max-width: 380px; line-height: 1.5; color: var(--c-muted); }

    .crm-chat-head {
      padding: 14px 22px;
      border-bottom: 1px solid var(--c-line);
      display: flex; align-items: center; gap: 14px;
      background: #fff;
      flex-shrink: 0;
    }
    .crm-back {
      display: none; /* solo se muestra en mobile, ver media query */
      background: transparent; border: 0;
      font-size: 20px; cursor: pointer; line-height: 1;
      color: var(--c-muted); padding: 6px 10px;
      border-radius: 8px;
      transition: all 0.12s;
    }
    .crm-back:hover { background: var(--c-paper-2); color: var(--c-ink); }
    /* crm-chat-head-avatar: legacy, ya no se renderea. Reemplazado por <Avatar />. */
    .crm-chat-head-body { flex: 1; min-width: 0; }
    .crm-chat-head-name {
      font-size: 16px; font-weight: 700;
      color: var(--c-ink);
      letter-spacing: -0.015em;
      line-height: 1.2;
    }
    .crm-chat-head-meta {
      font-size: 12px; color: var(--c-muted);
      margin-top: 5px;
      display: flex; align-items: center; gap: 7px;
      flex-wrap: wrap;
    }
    .crm-canal-id {
      display: inline-flex; align-items: center; gap: 5px;
      font-weight: 600;
      color: var(--c-ink);
    }
    .crm-canal-id-text { font-variant-numeric: tabular-nums; }
    .crm-meta-sep { color: var(--c-line); font-weight: 400; }
    .crm-otras-label { color: var(--c-muted); font-size: 11px; }
    .crm-otra-conv-pill {
      display: inline-flex; align-items: center; gap: 5px;
      background: var(--c-paper);
      border: 1px solid var(--c-line);
      color: var(--c-ink);
      font-size: 11px; font-weight: 600;
      padding: 3px 9px 3px 7px;
      border-radius: 999px;
      cursor: pointer;
      transition: all 0.12s;
      font-family: inherit;
    }
    .crm-otra-conv-pill:hover {
      background: #fff;
      border-color: var(--c-teal);
      color: var(--c-navy);
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 168, 181, 0.15);
    }
    .crm-pill-ventana {
      font-size: 11px; font-weight: 600;
      padding: 5px 11px;
      border-radius: 999px;
      letter-spacing: 0.01em;
      display: inline-flex; align-items: center; gap: 5px;
    }
    .crm-pill-ventana-ok { background: #DCFCE7; color: #14532D; }
    .crm-pill-ventana-ok::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--c-success); box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.2); }
    .crm-pill-ventana-cerrada { background: #FEF3C7; color: #78350F; }
    .crm-pill-ventana-cerrada::before { content: '⚠'; font-size: 10px; }

    .crm-online-pill {
      display: inline-flex; align-items: center; gap: 5px;
      background: #DCFCE7; color: #14532D;
      font-size: 10px; font-weight: 700;
      padding: 3px 9px;
      border-radius: 999px;
      letter-spacing: 0.02em;
      margin-left: 10px;
      text-transform: uppercase;
    }
    .crm-online-pill::before {
      content: '';
      width: 7px; height: 7px;
      border-radius: 50%;
      background: var(--c-success);
      box-shadow: 0 0 0 3px rgba(22,163,74,0.25);
      animation: crm-pulse 2s ease-in-out infinite;
    }
    @keyframes crm-pulse {
      0%, 100% { box-shadow: 0 0 0 3px rgba(22,163,74,0.25); }
      50% { box-shadow: 0 0 0 5px rgba(22,163,74,0.12); }
    }

    .crm-chat-head-actions { display: flex; align-items: center; gap: 8px; }
    .crm-icon-btn {
      background: var(--c-paper);
      border: 1px solid var(--c-line);
      color: var(--c-muted);
      padding: 8px 12px;
      border-radius: 10px;
      cursor: pointer;
      font-size: 14px;
      font-family: inherit;
      transition: all 0.12s;
    }
    .crm-icon-btn:hover { background: #fff; border-color: var(--c-muted-2); color: var(--c-ink); }

    /* Quick actions arriba del composer */
    .crm-quick {
      padding: 10px 22px;
      border-top: 1px solid var(--c-line);
      background: #fff;
      display: flex; gap: 6px;
      flex-wrap: wrap;
      flex-shrink: 0;
    }
    .crm-qr {
      background: #fff;
      border: 1px solid var(--c-line);
      color: var(--c-ink);
      padding: 7px 13px;
      border-radius: 999px;
      font-size: 12px; font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      display: inline-flex; align-items: center; gap: 5px;
      transition: all 0.12s;
      letter-spacing: -0.005em;
    }
    .crm-qr:hover { border-color: var(--c-teal); color: var(--c-teal); background: var(--c-teal-soft); }
    .crm-qr-tmpl {
      background: #EFF6FF;
      border-color: #BFDBFE;
      color: #1D4ED8;
    }
    .crm-qr-tmpl:hover { background: #DBEAFE; border-color: #60A5FA; color: #1E40AF; }

    .crm-chat-msgs {
      flex: 1; overflow-y: auto;
      padding: 24px 28px;
      background: var(--c-paper);
      display: flex; flex-direction: column;
      gap: 8px;
      background-image:
        radial-gradient(circle at 20% 30%, rgba(0, 168, 181, 0.02) 0%, transparent 40%),
        radial-gradient(circle at 80% 70%, rgba(26, 52, 97, 0.02) 0%, transparent 40%);
    }
    .crm-chat-msgs::-webkit-scrollbar { width: 8px; }
    .crm-chat-msgs::-webkit-scrollbar-thumb { background: rgba(15, 27, 54, 0.1); border-radius: 4px; }
    .crm-msg-empty { text-align: center; color: var(--c-muted-2); font-size: 12px; padding: 40px; }

    .crm-msg-sys {
      align-self: center;
      background: #FEF3C7;
      color: #78350F;
      font-size: 11px; font-weight: 600;
      padding: 7px 14px;
      border-radius: 999px;
      margin: 6px 0;
      letter-spacing: 0.01em;
    }
    .crm-msg-day-sep {
      align-self: center;
      color: var(--c-muted-2);
      font-size: 11px; font-weight: 600;
      padding: 4px 12px;
      margin: 12px 0 8px;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .crm-msg-wrap { display: flex; flex-direction: column; max-width: 65%; }
    .crm-msg-in { align-self: flex-start; }
    .crm-msg-out { align-self: flex-end; }
    .crm-msg {
      padding: 10px 14px;
      border-radius: 16px;
      font-size: 14px; line-height: 1.45;
      word-wrap: break-word;
      letter-spacing: -0.005em;
    }
    .crm-msg-in .crm-msg {
      background: #fff;
      color: var(--c-ink);
      border-bottom-left-radius: 4px;
      box-shadow: var(--shadow-bubble);
    }
    .crm-msg-out .crm-msg {
      background: linear-gradient(135deg, var(--c-teal) 0%, #0093A0 100%);
      color: #fff;
      border-bottom-right-radius: 4px;
      box-shadow: 0 1px 3px rgba(0, 168, 181, 0.25);
    }
    .crm-msg-sistema {
      background: linear-gradient(135deg, var(--c-navy-2), var(--c-navy)) !important;
      box-shadow: 0 1px 3px rgba(15, 27, 54, 0.2) !important;
    }
    .crm-msg-sys-tag {
      background: rgba(255,255,255,.18);
      color: #fff;
      font-size: 10px; font-weight: 700;
      padding: 2px 7px;
      border-radius: 4px;
      display: inline-block;
      margin-bottom: 5px;
      letter-spacing: 0.03em;
    }
    .crm-msg-media { font-size: 13px; font-weight: 600; opacity: 0.95; }
    .crm-msg-media-link { text-decoration: none; color: inherit; display: inline-block; padding: 6px 10px; border-radius: 8px; background: rgba(0,0,0,0.06); }
    .crm-msg-media-link:hover { background: rgba(0,0,0,0.10); }
    .crm-msg-imgcontainer { position: relative; display: inline-block; }
    .crm-msg-imgwrap { display: block; }
    .crm-msg-img { max-width: 260px; max-height: 320px; border-radius: 10px; display: block; }
    .crm-msg-usaravatar {
      position: absolute;
      bottom: 6px; left: 6px;
      background: rgba(15, 27, 54, 0.85);
      color: #fff;
      border: 0;
      padding: 4px 8px;
      border-radius: 6px;
      font-size: 10.5px;
      font-weight: 700;
      cursor: pointer;
      font-family: inherit;
      opacity: 0;
      transition: opacity 0.15s;
    }
    .crm-msg-imgcontainer:hover .crm-msg-usaravatar { opacity: 1; }
    .crm-msg-usaravatar:hover { background: var(--c-teal); }
    .crm-msg-audio { width: 240px; max-width: 100%; }
    .crm-msg-video { max-width: 260px; max-height: 320px; border-radius: 10px; display: block; background: #000; }
    .crm-msg-tmpl { font-size: 11px; opacity: 0.85; margin-bottom: 4px; }

    .crm-msg { position: relative; }
    .crm-msg-reaction {
      position: absolute;
      bottom: -8px;
      right: 8px;
      background: #fff;
      border: 1.5px solid var(--c-line);
      border-radius: 999px;
      padding: 1px 6px;
      font-size: 13px;
      line-height: 1;
      box-shadow: 0 2px 6px rgba(15, 27, 54, 0.10);
      cursor: default;
    }
    .crm-msg-in .crm-msg-reaction { right: auto; left: 8px; }

    /* Trigger de reacción (operador → cliente, solo burbujas inbound) */
    .crm-msg-react-trigger {
      position: absolute;
      top: -10px; right: -14px;
      z-index: 4;
    }
    .crm-msg-in .crm-msg-react-trigger { right: auto; left: -14px; }
    .crm-msg-react-btn {
      width: 28px; height: 28px;
      border-radius: 50%;
      background: #fff;
      border: 1.5px solid var(--c-line);
      cursor: pointer;
      font-size: 12px;
      box-shadow: 0 2px 6px rgba(15, 27, 54, 0.12);
      display: flex; align-items: center; justify-content: center;
      opacity: 0;
      transition: opacity 0.15s, transform 0.12s;
      line-height: 1;
      padding: 0;
    }
    .crm-msg:hover .crm-msg-react-btn { opacity: 1; }
    .crm-msg-react-btn:hover { transform: scale(1.1); border-color: var(--c-teal); }
    .crm-react-picker {
      position: absolute;
      top: 32px; left: 0;
      background: #fff;
      border: 1.5px solid var(--c-line);
      border-radius: 20px;
      box-shadow: 0 6px 18px rgba(15, 27, 54, 0.18);
      padding: 4px 6px;
      display: flex; gap: 2px;
      z-index: 10;
    }
    .crm-react-pick {
      background: transparent; border: 0;
      cursor: pointer;
      font-size: 20px;
      padding: 4px 6px;
      border-radius: 50%;
      transition: transform 0.1s;
      line-height: 1;
    }
    .crm-react-pick:hover { transform: scale(1.3); }

    /* Preview adjunto */
    .crm-preview-bg {
      position: fixed; inset: 0;
      background: rgba(15, 27, 54, 0.72);
      z-index: 200;
      display: flex; align-items: center; justify-content: center;
      padding: 24px;
    }
    .crm-preview-modal {
      background: #fff;
      border-radius: 14px;
      max-width: 540px;
      width: 100%;
      max-height: 86vh;
      display: flex; flex-direction: column;
      overflow: hidden;
      position: relative;
      box-shadow: 0 24px 60px rgba(15, 27, 54, 0.3);
    }
    .crm-preview-close {
      position: absolute;
      top: 10px; right: 12px;
      background: rgba(255, 255, 255, 0.9);
      border: 0;
      width: 32px; height: 32px;
      border-radius: 50%;
      font-size: 20px;
      cursor: pointer;
      z-index: 1;
      color: var(--c-ink);
      line-height: 1;
    }
    .crm-preview-close:hover { background: #fff; }
    .crm-preview-media {
      flex: 1;
      background: #0f1f3d;
      display: flex; align-items: center; justify-content: center;
      min-height: 200px;
      max-height: 60vh;
      overflow: hidden;
    }
    .crm-preview-media img,
    .crm-preview-media video {
      max-width: 100%; max-height: 60vh;
      object-fit: contain;
      display: block;
    }
    .crm-preview-doc {
      padding: 32px;
      display: flex; align-items: center; gap: 16px;
      color: #fff;
      width: 100%;
    }
    .crm-preview-doc-icon { font-size: 48px; }
    .crm-preview-doc-info { flex: 1; }
    .crm-preview-doc-name { font-weight: 700; font-size: 15px; word-break: break-word; }
    .crm-preview-doc-meta { font-size: 12px; opacity: 0.7; margin-top: 4px; }
    .crm-preview-footer {
      padding: 12px 14px;
      display: flex; gap: 8px;
      border-top: 1px solid var(--c-line);
      background: #fff;
    }
    .crm-preview-caption {
      flex: 1;
      padding: 9px 12px;
      border: 1px solid var(--c-line);
      border-radius: 8px;
      font-size: 14px;
      font-family: inherit;
      color: var(--c-ink);
    }
    .crm-preview-caption:focus { outline: 2px solid var(--c-teal); outline-offset: -1px; }
    .crm-preview-btn-cancel {
      background: transparent; border: 1px solid var(--c-line);
      padding: 9px 14px;
      border-radius: 8px;
      font-size: 13px; font-weight: 700;
      color: var(--c-muted-2); cursor: pointer;
      font-family: inherit;
    }
    .crm-preview-btn-cancel:hover { background: var(--c-paper-2); }
    .crm-preview-btn-send {
      background: var(--c-teal); color: #fff;
      border: 0;
      padding: 9px 18px;
      border-radius: 8px;
      font-size: 13px; font-weight: 800;
      cursor: pointer; font-family: inherit;
    }
    .crm-preview-btn-send:hover { background: #008a95; }
    .crm-preview-btn-send:disabled, .crm-preview-btn-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
    .crm-msg-texto { white-space: pre-wrap; }
    .crm-msg-meta {
      font-size: 11px; color: var(--c-muted-2);
      margin-top: 4px;
      padding: 0 8px;
      display: flex; align-items: center; gap: 6px;
      font-variant-numeric: tabular-nums;
    }
    .crm-msg-out .crm-msg-meta { justify-content: flex-end; }

    .crm-error-bar {
      background: #FEE2E2; color: #991B1B;
      padding: 10px 18px;
      font-size: 12px; font-weight: 600;
      display: flex; justify-content: space-between; align-items: center;
      border-top: 1px solid #FCA5A5;
    }
    .crm-error-bar button {
      background: transparent; border: 0;
      cursor: pointer; color: #991B1B;
      font-size: 16px; padding: 2px 8px;
      border-radius: 4px;
    }
    .crm-error-bar button:hover { background: rgba(220, 38, 38, 0.1); }

    .crm-composer {
      padding: 14px 22px 18px;
      border-top: 1px solid var(--c-line);
      background: #fff;
      display: flex; align-items: end;
      gap: 10px;
      flex-shrink: 0;
    }
    .crm-composer-attach {
      background: transparent; border: 0;
      font-size: 18px; cursor: pointer;
      color: var(--c-muted-2);
      padding: 10px;
      border-radius: 10px;
      transition: all 0.12s;
    }
    .crm-composer-attach:hover:not(:disabled) { background: var(--c-paper-2); color: var(--c-ink); }
    .crm-composer-attach:disabled { opacity: 0.35; cursor: not-allowed; }
    .crm-composer-box {
      flex: 1;
      position: relative;
    }
    .crm-composer-input {
      width: 100%;
      border: 1px solid var(--c-line);
      background: var(--c-paper);
      border-radius: 14px;
      padding: 12px 16px;
      font-family: inherit;
      font-size: 14px;
      color: var(--c-ink);
      resize: none;
      line-height: 1.45;
      max-height: 140px;
      outline: none;
      transition: all 0.15s;
    }
    .crm-composer-input:focus {
      background: #fff;
      border-color: var(--c-teal);
      box-shadow: var(--shadow-focus);
    }
    .crm-composer-input::placeholder { color: var(--c-muted-2); }
    .crm-composer-input:disabled { background: var(--c-paper-2); color: var(--c-muted-2); cursor: not-allowed; }

    /* Dropdown de respuestas rápidas */
    .crm-slash-dropdown {
      position: absolute;
      bottom: calc(100% + 8px);
      left: 0; right: 0;
      background: #fff;
      border: 1px solid var(--c-line);
      border-radius: 14px;
      box-shadow: 0 10px 30px rgba(15, 27, 54, 0.12), 0 4px 8px rgba(15, 27, 54, 0.04);
      overflow: hidden;
      max-height: 360px;
      display: flex;
      flex-direction: column;
      z-index: 100;
    }
    .crm-slash-head {
      padding: 10px 14px;
      border-bottom: 1px solid var(--c-line);
      background: var(--c-paper);
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 12px;
      font-weight: 700;
      color: var(--c-ink);
      letter-spacing: -0.005em;
    }
    .crm-slash-hint {
      font-size: 10px;
      color: var(--c-muted-2);
      font-weight: 500;
      letter-spacing: 0;
    }
    .crm-slash-item {
      background: transparent;
      border: 0;
      padding: 10px 14px;
      cursor: pointer;
      text-align: left;
      font-family: inherit;
      transition: background 0.1s;
      border-bottom: 1px solid var(--c-line-soft);
    }
    .crm-slash-item:last-child { border-bottom: 0; }
    .crm-slash-item:hover,
    .crm-slash-item.is-active {
      background: var(--c-teal-soft);
    }
    .crm-slash-item-head {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 3px;
    }
    .crm-slash-slug {
      font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
      font-size: 11px;
      font-weight: 700;
      color: var(--c-teal);
      background: rgba(0, 168, 181, 0.1);
      padding: 1px 6px;
      border-radius: 4px;
      letter-spacing: -0.02em;
    }
    .crm-slash-titulo {
      font-size: 13px;
      font-weight: 600;
      color: var(--c-ink);
      flex: 1;
      letter-spacing: -0.005em;
    }
    .crm-slash-cat {
      font-size: 9px;
      font-weight: 700;
      padding: 2px 6px;
      border-radius: 4px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .crm-slash-cat-info { background: #DBEAFE; color: #1E40AF; }
    .crm-slash-cat-soporte { background: #FEE2E2; color: #991B1B; }
    .crm-slash-cat-comercial { background: #DCFCE7; color: #14532D; }
    .crm-slash-cat-logistica { background: #FEF3C7; color: #92400E; }
    .crm-slash-preview {
      font-size: 12px;
      color: var(--c-muted);
      line-height: 1.4;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .crm-composer-send {
      background: linear-gradient(135deg, var(--c-teal) 0%, #0093A0 100%);
      color: #fff; border: 0;
      border-radius: 50%;
      width: 42px; height: 42px;
      cursor: pointer;
      font-size: 16px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
      box-shadow: 0 2px 6px rgba(0, 168, 181, 0.3);
      transition: all 0.12s;
    }
    .crm-composer-send:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 10px rgba(0, 168, 181, 0.4); }
    .crm-composer-send:active:not(:disabled) { transform: translateY(0); }
    .crm-composer-send:disabled {
      background: var(--c-line);
      color: var(--c-muted-2);
      box-shadow: none;
      cursor: not-allowed;
    }

    /* CTA grande cuando la ventana 24h está cerrada — reemplaza el textarea + adjunto + envío */
    .crm-composer-template-cta {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 14px;
      width: 100%;
      background: linear-gradient(135deg, rgba(0,168,181,.10) 0%, rgba(0,168,181,.04) 100%);
      border: 1px dashed var(--c-teal);
      border-radius: 12px;
      padding: 12px 18px;
      cursor: pointer;
      font-family: inherit;
      transition: all .15s;
      text-align: left;
    }
    .crm-composer-template-cta:hover {
      background: linear-gradient(135deg, rgba(0,168,181,.18) 0%, rgba(0,168,181,.08) 100%);
      border-style: solid;
      transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(0,168,181,.18);
    }
    .crm-composer-template-cta:active { transform: translateY(0); }
    .crm-composer-template-icon {
      font-size: 22px;
      flex-shrink: 0;
    }
    .crm-composer-template-label {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }
    .crm-composer-template-label strong {
      color: var(--c-teal);
      font-size: 14px;
      font-weight: 700;
      letter-spacing: -.01em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .crm-composer-template-sub {
      color: var(--c-muted);
      font-size: 12px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .crm-composer-template-arrow {
      color: var(--c-teal);
      font-size: 20px;
      font-weight: 800;
      flex-shrink: 0;
      transition: transform .2s;
    }
    .crm-composer-template-cta:hover .crm-composer-template-arrow { transform: translateX(4px); }

    /* ─────────── Ficha cliente ─────────── */
    .crm-cliente {
      background: #fff;
      border-left: 1px solid var(--c-line);
      padding: 24px 20px;
      overflow-y: auto;
      min-height: 0;
    }
    .crm-cliente::-webkit-scrollbar { width: 6px; }
    .crm-cliente::-webkit-scrollbar-thumb { background: var(--c-line); border-radius: 4px; }

    .crm-cliente-empty {
      text-align: center; color: var(--c-muted-2);
      padding: 60px 18px;
      font-size: 13px;
      line-height: 1.5;
    }
    .crm-cliente-empty .crm-empty-icon { font-size: 40px; margin-bottom: 12px; }

    .crm-cliente-content { display: flex; flex-direction: column; gap: 18px; }

    /* Hero del cliente */
    .crm-cliente-hero {
      display: flex; flex-direction: column; align-items: center;
      text-align: center;
      padding-bottom: 18px;
      border-bottom: 1px solid var(--c-line);
    }
    .crm-cliente-hero-avatar-wrap {
      position: relative;
      width: 88px; height: 88px;
      margin-bottom: 12px;
      box-shadow: 0 4px 12px rgba(15, 27, 54, 0.15);
      border-radius: 50%;
    }
    .crm-cliente-hero-avatar-actions {
      position: absolute;
      bottom: -2px; right: -2px;
      display: flex;
      gap: 4px;
    }
    .crm-cliente-avatar-btn {
      background: #fff;
      border: 1.5px solid var(--c-line);
      width: 30px; height: 30px;
      border-radius: 50%;
      cursor: pointer;
      font-size: 13px;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 6px rgba(15, 27, 54, 0.12);
      transition: transform 0.12s;
      color: var(--c-ink);
    }
    .crm-cliente-avatar-btn:hover { transform: scale(1.08); border-color: var(--c-teal); }
    .crm-cliente-avatar-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .crm-cliente-avatar-btn-x { color: #b91c1c; border-color: #fecaca; font-weight: 900; }
    .crm-cliente-avatar-btn-x:hover { background: #fee2e2; border-color: #fca5a5; }

    /* Badge de canal arriba del avatar — claro y diferenciado del botón cámara */
    .crm-cliente-canal-badge {
      position: absolute; top: -2px; left: -2px;
      width: 26px; height: 26px;
      background: #fff; border: 2px solid #fff; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 1px 4px rgba(0,0,0,0.12);
      z-index: 2;
    }

    /* Selector de tipo de contacto */
    .crm-contacto-tipo-btn {
      font-family: inherit;
      font-size: 11.5px; font-weight: 700;
      padding: 5px 10px; border-radius: 999px;
      border: 1.5px solid;
      cursor: pointer;
      transition: all 0.15s;
    }
    .crm-contacto-tipo-btn:hover:not(:disabled) { transform: translateY(-1px); }
    .crm-contacto-tipo-btn:disabled { opacity: 0.6; cursor: wait; }
    .crm-contacto-input {
      width: 100%;
      box-sizing: border-box;
      margin-top: 4px;
      padding: 6px 10px;
      font-family: inherit;
      font-size: 13px;
      border: 1px solid #E2E8F0;
      border-radius: 6px;
      background: #fff;
      resize: vertical;
    }
    .crm-contacto-input:focus { outline: none; border-color: #1A3461; box-shadow: 0 0 0 2px #1A346122; }

    .crm-cliente-h {
      font-size: 17px; font-weight: 700;
      color: var(--c-ink);
      letter-spacing: -0.015em;
    }
    .crm-cliente-sub {
      font-size: 12px; color: var(--c-muted);
      margin-top: 4px;
      line-height: 1.4;
    }
    .crm-cliente-badges {
      display: flex; gap: 6px;
      margin-top: 10px;
      flex-wrap: wrap; justify-content: center;
    }
    .crm-cliente-badge {
      font-size: 10px; font-weight: 700;
      padding: 3px 10px;
      border-radius: 999px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .crm-cliente-badge-ok { background: #DCFCE7; color: #14532D; }
    .crm-cliente-badge-warn { background: #FEF3C7; color: #78350F; }
    .crm-cliente-badge-info { background: var(--c-teal-soft); color: var(--c-teal); }

    .crm-cliente-box {
      background: var(--c-paper);
      border: 1px solid var(--c-line);
      border-radius: 12px;
      padding: 4px 14px;
      box-shadow: var(--shadow-card);
    }
    .crm-cliente-row {
      display: flex; justify-content: space-between; align-items: center;
      padding: 10px 0;
      font-size: 13px; color: var(--c-muted);
    }
    .crm-cliente-row + .crm-cliente-row { border-top: 1px solid var(--c-line); }
    .crm-cliente-row strong {
      color: var(--c-ink); font-weight: 600;
      font-variant-numeric: tabular-nums;
    }

    .crm-cliente-actions { display: flex; flex-direction: column; gap: 6px; }
    .crm-cliente-btn {
      background: #fff;
      border: 1px solid var(--c-line);
      color: var(--c-ink);
      padding: 11px 14px;
      border-radius: 10px;
      font-size: 13px; font-weight: 600;
      cursor: pointer; text-align: left;
      font-family: inherit; text-decoration: none;
      transition: all 0.12s;
      display: flex; align-items: center; gap: 8px;
    }
    .crm-cliente-btn:hover {
      border-color: var(--c-teal);
      color: var(--c-teal);
      background: var(--c-teal-soft);
      transform: translateX(2px);
    }
    .crm-cliente-btn-primary {
      background: linear-gradient(135deg, var(--c-navy), var(--c-navy-2)) !important;
      color: #fff !important;
      border-color: var(--c-navy) !important;
      box-shadow: 0 2px 8px rgba(26, 52, 97, 0.18);
    }
    .crm-cliente-btn-primary:hover {
      background: linear-gradient(135deg, var(--c-navy-2), var(--c-teal)) !important;
      color: #fff !important;
      border-color: var(--c-teal) !important;
    }
    .crm-cliente-desvincular-wrap {
      margin-top: 18px;
      padding-top: 16px;
      border-top: 1px dashed var(--c-line);
      text-align: center;
    }
    .crm-cliente-desvincular-btn {
      background: transparent;
      border: 0;
      color: var(--c-muted);
      font-size: 11px;
      cursor: pointer;
      padding: 4px 8px;
      font-family: inherit;
      text-decoration: underline;
      text-underline-offset: 3px;
      transition: color 0.12s;
    }
    .crm-cliente-desvincular-btn:hover { color: #DC2626; }
    .crm-cliente-label {
      font-size: 10px; font-weight: 700;
      color: var(--c-muted-2);
      letter-spacing: 0.12em;
      text-transform: uppercase;
      margin-top: 4px;
    }
    .crm-timeline { display: flex; flex-direction: column; gap: 6px; }
    .crm-timeline-empty { font-size: 12px; color: var(--c-muted-2); padding: 12px 0; }
    .crm-timeline-item {
      background: var(--c-paper);
      border-left: 3px solid var(--c-muted-2);
      padding: 10px 14px;
      display: flex; gap: 10px;
      border-radius: 0 8px 8px 0;
      transition: background 0.12s;
    }
    .crm-timeline-item:hover { background: var(--c-paper-2); }
    .crm-timeline-icon { font-size: 16px; flex-shrink: 0; }
    .crm-timeline-body { flex: 1; min-width: 0; }
    .crm-timeline-titulo { font-size: 13px; font-weight: 600; color: var(--c-ink); letter-spacing: -0.01em; }
    .crm-timeline-desc { font-size: 12px; color: var(--c-muted); margin-top: 3px; line-height: 1.4; }
    .crm-timeline-fecha { font-size: 11px; color: var(--c-muted-2); margin-top: 5px; font-variant-numeric: tabular-nums; }

    /* ─────────── F7: Match en mensaje histórico ─────────── */
    .crm-conv-historic {
      position: absolute; top: -4px; right: -4px;
      width: 18px; height: 18px; border-radius: 50%;
      background: #FEF3C7; border: 2px solid #fff;
      font-size: 10px;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 1px 3px rgba(0,0,0,0.15);
    }
    .crm-conv.is-historic-match {
      background: rgba(254, 243, 199, 0.25);
    }
    .crm-conv.is-historic-match:hover {
      background: rgba(254, 243, 199, 0.5);
    }

    /* ─────────── F11: Nueva conversación (botón + modal) ─────────── */
    .crm-topbar-cta {
      margin-left: 14px;
      background: var(--c-teal);
      color: #fff;
      border: 0; border-radius: 8px;
      padding: 8px 14px;
      font-size: 13px; font-weight: 700;
      cursor: pointer; font-family: inherit;
      transition: background 0.12s;
    }
    .crm-topbar-cta:hover { background: #008a95; }

    .crm-modal-overlay {
      position: fixed; inset: 0; z-index: 9000;
      background: rgba(15, 27, 54, 0.55);
      backdrop-filter: blur(3px);
      display: flex; align-items: flex-start; justify-content: center;
      padding-top: 6vh;
      animation: crmModalFade 0.18s ease-out;
    }
    @keyframes crmModalFade { from { opacity: 0 } to { opacity: 1 } }
    .crm-modal-card {
      background: #fff;
      border-radius: 14px;
      width: 92%; max-width: 600px;
      max-height: 84vh;
      display: flex; flex-direction: column;
      box-shadow: 0 20px 60px rgba(15,27,54,0.4);
      overflow: hidden;
      animation: crmModalSlide 0.22s ease-out;
    }
    @keyframes crmModalSlide {
      from { transform: translateY(-10px); opacity: 0 }
      to { transform: translateY(0); opacity: 1 }
    }
    .crm-modal-head {
      padding: 14px 18px;
      border-bottom: 1px solid var(--c-line);
      display: flex; justify-content: space-between; align-items: center;
    }
    .crm-modal-head h2 {
      font-size: 18px; font-weight: 800; color: var(--c-ink); margin: 0;
    }
    .crm-modal-close {
      background: transparent; border: 0; cursor: pointer;
      font-size: 24px; line-height: 1; color: var(--c-muted);
      padding: 4px 8px;
    }
    .crm-modal-close:hover { color: var(--c-ink); }
    .crm-modal-body {
      padding: 16px 18px;
      overflow-y: auto;
      flex: 1;
    }
    .crm-modal-hint {
      font-size: 12px; color: var(--c-muted);
      margin: 0 0 12px;
      padding: 10px 12px;
      background: var(--c-teal-soft);
      border-left: 3px solid var(--c-teal);
      border-radius: 6px;
      line-height: 1.5;
    }
    .crm-modal-tabs {
      display: flex; gap: 4px;
      margin-bottom: 14px;
      background: var(--c-paper-2);
      padding: 3px; border-radius: 8px;
    }
    .crm-modal-tab {
      flex: 1;
      padding: 8px 12px;
      background: transparent;
      border: 0; border-radius: 6px;
      font-size: 13px; font-weight: 600;
      color: var(--c-muted); cursor: pointer;
      font-family: inherit;
      transition: all 0.12s;
    }
    .crm-modal-tab:hover { color: var(--c-ink); }
    .crm-modal-tab.is-active {
      background: #fff; color: var(--c-ink);
      box-shadow: 0 1px 3px rgba(0,0,0,0.08);
    }
    .crm-modal-section { margin-bottom: 14px; }
    .crm-modal-label {
      display: block;
      font-size: 11px; font-weight: 700;
      color: var(--c-muted); text-transform: uppercase;
      letter-spacing: 0.06em; margin-bottom: 6px;
    }
    .crm-modal-input {
      width: 100%; box-sizing: border-box;
      padding: 9px 12px;
      border: 1.5px solid var(--c-line);
      border-radius: 7px;
      font-size: 14px; font-family: inherit;
      color: var(--c-ink);
    }
    .crm-modal-input:focus {
      outline: none;
      border-color: var(--c-teal);
      box-shadow: var(--shadow-focus);
    }
    .crm-modal-list {
      margin-top: 8px;
      max-height: 200px; overflow-y: auto;
      border: 1px solid var(--c-line);
      border-radius: 7px;
    }
    .crm-modal-item {
      display: flex; align-items: center;
      width: 100%;
      padding: 10px 12px;
      border: 0; background: transparent;
      cursor: pointer; text-align: left;
      font-family: inherit;
      border-bottom: 1px solid var(--c-line-soft);
      transition: background 0.1s;
    }
    .crm-modal-item:last-child { border-bottom: 0; }
    .crm-modal-item:hover { background: var(--c-paper-2); }
    .crm-modal-item.is-selected { background: var(--c-teal-soft); }
    .crm-modal-item-name {
      font-size: 13px; font-weight: 700; color: var(--c-ink);
    }
    .crm-modal-item-sub {
      font-size: 11px; color: var(--c-muted); margin-top: 2px;
    }
    .crm-modal-empty {
      padding: 14px; text-align: center;
      color: var(--c-muted); font-size: 13px; font-style: italic;
      background: var(--c-paper-2); border-radius: 7px;
    }
    .crm-modal-tpls {
      display: flex; flex-direction: column; gap: 6px;
      max-height: 240px; overflow-y: auto;
    }
    .crm-modal-tpl {
      padding: 10px 12px;
      border: 1.5px solid var(--c-line);
      border-radius: 8px;
      background: #fff; cursor: pointer;
      text-align: left;
      font-family: inherit;
      transition: all 0.12s;
    }
    .crm-modal-tpl:hover { border-color: var(--c-teal); background: var(--c-paper-2); }
    .crm-modal-tpl.is-selected {
      border-color: var(--c-teal);
      background: var(--c-teal-soft);
    }
    .crm-modal-tpl-name {
      font-size: 13px; font-weight: 700; color: var(--c-ink);
      display: flex; align-items: center; gap: 8px;
    }
    .crm-modal-tpl-cat {
      font-size: 9px; font-weight: 800;
      padding: 1px 6px; border-radius: 4px;
      text-transform: uppercase; letter-spacing: 0.04em;
    }
    .crm-modal-tpl-cat-utility { background: #DBEAFE; color: #1E40AF; }
    .crm-modal-tpl-cat-marketing { background: #FCE7F3; color: #9F1239; }
    .crm-modal-tpl-cat-authentication { background: #E0E7FF; color: #4338CA; }
    .crm-modal-tpl-body {
      font-size: 12px; color: var(--c-muted);
      margin-top: 4px; line-height: 1.4;
      white-space: pre-wrap;
    }
    .crm-modal-preview {
      padding: 12px 14px;
      background: #DCFCE7;
      border-left: 3px solid #16A34A;
      border-radius: 7px;
      font-size: 13px; color: #14532D;
      line-height: 1.55;
      white-space: pre-wrap;
    }
    .crm-modal-error {
      padding: 10px 12px;
      background: #FEE2E2;
      color: #991B1B;
      border-radius: 7px;
      font-size: 13px; font-weight: 600;
      margin-bottom: 8px;
    }
    .crm-modal-foot {
      padding: 12px 18px;
      border-top: 1px solid var(--c-line);
      display: flex; justify-content: flex-end; gap: 8px;
    }
    .crm-modal-btn-ghost {
      padding: 8px 16px;
      background: transparent;
      border: 1px solid var(--c-line);
      border-radius: 7px;
      font-size: 13px; font-weight: 700;
      color: var(--c-muted); cursor: pointer;
      font-family: inherit;
    }
    .crm-modal-btn-ghost:hover { color: var(--c-ink); background: var(--c-paper-2); }
    .crm-modal-btn-primary {
      padding: 8px 18px;
      background: var(--c-teal);
      color: #fff;
      border: 0; border-radius: 7px;
      font-size: 13px; font-weight: 700;
      cursor: pointer; font-family: inherit;
      transition: all 0.12s;
    }
    .crm-modal-btn-primary:hover:not(:disabled) { background: #008a95; }
    .crm-modal-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

    /* ─────────── F6: Auto-tags (read-only, distinguibles) ─────────── */
    .crm-tag-chip-auto, .crm-conv-tag-auto {
      cursor: default !important;
      font-weight: 700;
      border: 1px dashed currentColor;
    }
    .crm-tag-chip-auto::before, .crm-conv-tag-auto::before {
      content: "⚙ ";
      font-size: 9px; opacity: 0.5;
      margin-right: -1px;
    }
    /* Colores por tipo de auto-tag */
    .crm-auto-tag-pausada, .crm-tag-chip-auto.crm-auto-tag-pausada {
      background: #FEF3C7 !important; color: #92400E !important;
    }
    .crm-auto-tag-cancelada, .crm-tag-chip-auto.crm-auto-tag-cancelada {
      background: #F1F5F9 !important; color: #64748B !important;
    }
    .crm-auto-tag-b2b, .crm-tag-chip-auto.crm-auto-tag-b2b {
      background: #DBEAFE !important; color: #1E40AF !important;
    }
    .crm-auto-tag-pago-fallido, .crm-tag-chip-auto.crm-auto-tag-pago-fallido,
    .crm-auto-tag-cobro-vencido, .crm-tag-chip-auto.crm-auto-tag-cobro-vencido {
      background: #FEE2E2 !important; color: #991B1B !important;
    }
    .crm-auto-tag-vip, .crm-tag-chip-auto.crm-auto-tag-vip {
      background: linear-gradient(135deg, #FEF3C7, #FCD34D) !important; color: #7C2D12 !important;
    }
    .crm-auto-tag-nuevo, .crm-tag-chip-auto.crm-auto-tag-nuevo {
      background: var(--c-teal-soft) !important; color: #0e7490 !important;
    }

    /* ─────────── F5: KPIs ficha cliente ─────────── */
    .crm-kpi-chips {
      display: flex; flex-wrap: wrap; gap: 6px;
      padding: 0 14px 12px;
    }
    .crm-kpi-chip {
      display: inline-flex; flex-direction: column; align-items: center;
      padding: 6px 10px; border-radius: 8px;
      background: var(--c-paper-2);
      border: 1px solid var(--c-line);
      flex: 1; min-width: 70px;
    }
    .crm-kpi-chip.is-warn {
      background: #FEF3C7; border-color: #FCD34D;
    }
    .crm-kpi-chip.is-warn .crm-kpi-val { color: #92400E; }
    .crm-kpi-chip.is-warn .crm-kpi-lbl { color: #B45309; }
    .crm-kpi-val {
      font-size: 15px; font-weight: 800;
      color: var(--c-ink);
      letter-spacing: -0.015em; line-height: 1.1;
    }
    .crm-kpi-lbl {
      font-size: 9px; font-weight: 700;
      color: var(--c-muted);
      text-transform: uppercase; letter-spacing: 0.06em;
      margin-top: 2px;
    }

    .crm-cobro-pill {
      display: inline-block; margin-left: 4px;
      font-size: 10px; font-weight: 700;
      padding: 1px 7px; border-radius: 999px;
      vertical-align: middle;
    }
    .crm-cobro-pill.is-vencido { background: #FEE2E2; color: #991B1B; }
    .crm-cobro-pill.is-hoy { background: #FEF3C7; color: #92400E; }
    .crm-cobro-pill.is-cerca { background: var(--c-teal-soft); color: #0e7490; }

    .crm-pago-estado {
      font-weight: 700; text-transform: capitalize;
    }
    .crm-pago-estado-pagado { color: #16A34A; }
    .crm-pago-estado-fallido { color: #DC2626; }
    .crm-pago-estado-atrasado { color: #D97706; }
    .crm-pago-estado-pendiente { color: var(--c-muted); }

    /* ─────────── F4: Pulido visual ─────────── */
    /* Loading skeleton para inbox */
    .crm-skel { padding: 6px 8px; display: flex; flex-direction: column; gap: 4px; }
    .crm-skel-item {
      display: flex; gap: 10px; padding: 12px 10px;
      animation: crmSkelPulse 1.4s ease-in-out infinite;
      opacity: 0; animation-fill-mode: backwards;
    }
    .crm-skel-avatar {
      width: 38px; height: 38px; border-radius: 50%;
      background: linear-gradient(90deg, #E2E8F0, #F1F5F9, #E2E8F0);
      background-size: 200% 100%;
      animation: crmSkelShimmer 1.4s linear infinite;
    }
    .crm-skel-body { flex: 1; display: flex; flex-direction: column; gap: 6px; }
    .crm-skel-line {
      height: 10px; border-radius: 4px;
      background: linear-gradient(90deg, #E2E8F0, #F1F5F9, #E2E8F0);
      background-size: 200% 100%;
      animation: crmSkelShimmer 1.4s linear infinite;
    }
    .crm-skel-line-lg { width: 65%; height: 12px; }
    .crm-skel-line-md { width: 85%; }
    .crm-skel-line-sm { width: 40%; height: 8px; }
    @keyframes crmSkelShimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    @keyframes crmSkelPulse {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }

    /* Banner pinned por tags importantes */
    .crm-pinned {
      display: flex; align-items: center; gap: 10px;
      padding: 9px 16px;
      border-left: 4px solid;
      font-size: 12px; font-weight: 700;
      letter-spacing: 0.01em;
      animation: crmSlideDown 0.25s ease-out;
    }
    @keyframes crmSlideDown {
      from { transform: translateY(-100%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    /* Smooth scroll en lista de mensajes */
    .crm-chat-msgs { scroll-behavior: smooth; }

    /* Animación de entrada de mensajes nuevos */
    .crm-msg-wrap {
      animation: crmMsgIn 0.22s ease-out;
    }
    @keyframes crmMsgIn {
      from { transform: translateY(8px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    /* Hover sutil en ConversacionItem */
    .crm-conv { transition: background 0.12s, transform 0.12s; }
    .crm-conv:hover:not(.is-active) { transform: translateX(2px); }

    /* ─────────── F3: Sugerencia Claude ─────────── */
    .crm-sug {
      margin: 0 14px 8px;
      padding: 10px 12px;
      background: linear-gradient(135deg, #FAFAFC, #F1F5F9);
      border: 1px solid #DDD6FE;
      border-left: 3px solid #7C3AED;
      border-radius: 10px;
      animation: crmPopIn 0.2s ease-out;
    }
    .crm-sug-loading {
      display: flex; align-items: center; gap: 10px;
      font-size: 12px; color: var(--c-muted); font-style: italic;
    }
    .crm-sug-spinner {
      width: 14px; height: 14px; border-radius: 50%;
      border: 2px solid #DDD6FE; border-top-color: #7C3AED;
      animation: crmSpin 0.7s linear infinite;
    }
    @keyframes crmSpin { to { transform: rotate(360deg); } }
    .crm-sug-error {
      display: flex; justify-content: space-between; align-items: center;
      font-size: 12px; color: #92400E;
      background: #FEF3C7; border-color: #FCD34D; border-left-color: #D97706;
    }
    .crm-sug-head {
      display: flex; align-items: center; gap: 8px;
      margin-bottom: 6px;
    }
    .crm-sug-icon { font-size: 14px; }
    .crm-sug-titulo {
      font-size: 11px; font-weight: 800; color: #5B21B6;
      text-transform: uppercase; letter-spacing: 0.08em;
    }
    .crm-sug-tag {
      font-size: 10px; font-weight: 700;
      background: #EDE9FE; color: #5B21B6;
      padding: 2px 7px; border-radius: 999px;
      text-transform: lowercase;
    }
    .crm-sug-conf {
      font-size: 10px; font-weight: 700;
      padding: 1px 7px; border-radius: 999px;
      border: 1px solid;
      background: rgba(255,255,255,0.6);
    }
    .crm-sug-x {
      margin-left: auto;
      background: transparent; border: 0; cursor: pointer;
      font-size: 16px; color: var(--c-muted);
      padding: 0; width: 22px; height: 22px;
      border-radius: 50%; transition: background 0.12s;
    }
    .crm-sug-x:hover { background: rgba(0,0,0,0.05); color: var(--c-ink); }
    .crm-sug-texto {
      font-size: 13px; color: var(--c-ink); line-height: 1.55;
      padding: 8px 10px; background: #fff; border-radius: 8px;
      margin-bottom: 8px;
      white-space: pre-wrap;
    }
    .crm-sug-actions {
      display: flex; align-items: center; gap: 10px;
    }
    .crm-sug-btn-primary {
      padding: 6px 14px; border-radius: 6px;
      background: #7C3AED; color: #fff;
      font-size: 12px; font-weight: 700;
      border: 0; cursor: pointer; font-family: inherit;
      transition: all 0.12s;
    }
    .crm-sug-btn-primary:hover { background: #6D28D9; }
    .crm-sug-hint {
      font-size: 11px; color: var(--c-muted); font-style: italic;
    }

    .crm-composer-suggest {
      background: transparent; border: 1px solid var(--c-line);
      width: 38px; height: 38px; border-radius: 50%;
      cursor: pointer; font-size: 17px;
      display: flex; align-items: center; justify-content: center;
      color: #7C3AED; transition: all 0.12s;
    }
    .crm-composer-suggest:hover:not(:disabled) {
      border-color: #7C3AED; background: #FAF5FF;
    }
    .crm-composer-suggest:disabled { opacity: 0.4; cursor: not-allowed; }

    /* ─────────── F2: meta conversación en ficha + filtros timeline ─────────── */
    .crm-meta-conv .crm-cliente-row { gap: 10px; }
    .crm-meta-assignee {
      display: inline-flex; align-items: center; gap: 6px;
      font-size: 12px;
    }
    .crm-meta-tags {
      display: flex; flex-wrap: wrap; gap: 4px; justify-content: flex-end;
    }
    .crm-tag-chip-static {
      display: inline-flex; align-items: center;
      background: var(--c-teal-soft); color: #0e7490;
      font-size: 10px; font-weight: 700;
      padding: 2px 7px; border-radius: 999px;
    }
    .crm-meta-notas {
      margin-top: 8px; padding-top: 10px;
      border-top: 1px dashed var(--c-line);
    }
    .crm-meta-notas-label {
      font-size: 10px; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.08em; color: var(--c-warning);
      margin-bottom: 5px;
    }
    .crm-meta-notas-texto {
      font-size: 12px; color: var(--c-ink);
      line-height: 1.5; white-space: pre-wrap;
      background: #FFFBEB; padding: 8px 10px; border-radius: 6px;
      border-left: 3px solid var(--c-warning);
    }

    .crm-timeline-filtros {
      display: flex; flex-wrap: wrap; gap: 4px;
      padding: 0 12px 10px; margin-top: -4px;
    }
    .crm-timeline-filtro {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 4px 9px; border-radius: 999px;
      background: var(--c-paper-2); border: 1px solid transparent;
      font-size: 10px; font-weight: 700; letter-spacing: 0.03em;
      color: var(--c-muted); cursor: pointer;
      font-family: inherit; transition: all 0.12s;
    }
    .crm-timeline-filtro:hover:not(:disabled) {
      background: var(--c-teal-soft); color: #0e7490;
    }
    .crm-timeline-filtro.is-active {
      background: var(--c-navy-2); color: #fff;
    }
    .crm-timeline-filtro:disabled {
      opacity: 0.4; cursor: not-allowed;
    }
    .crm-timeline-filtro-count {
      background: rgba(255,255,255,0.2); color: inherit;
      padding: 1px 5px; border-radius: 999px; font-size: 9px;
      min-width: 14px; text-align: center;
    }
    .crm-timeline-filtro:not(.is-active) .crm-timeline-filtro-count {
      background: #fff; color: var(--c-muted);
    }

    /* ─────────── Badge de agente asignado en inbox ─────────── */
    .crm-conv-avatar { position: relative; }
    .crm-conv-assignee {
      position: absolute; bottom: -2px; right: -2px;
      width: 18px; height: 18px; border-radius: 50%;
      background: linear-gradient(135deg, #475569, #1e293b);
      color: #fff; font-size: 8px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      border: 2px solid #fff;
      letter-spacing: -0.02em;
    }

    /* ─────────── F1: tags, asignación, estado, notas ─────────── */
    .crm-chat-tags {
      display: flex; flex-wrap: wrap; gap: 5px; margin-top: 6px; align-items: center;
      position: relative;
    }
    .crm-tag-chip {
      display: inline-flex; align-items: center; gap: 3px;
      background: var(--c-teal-soft); color: #0e7490;
      font-size: 11px; font-weight: 700; letter-spacing: 0.01em;
      padding: 2px 8px 2px 8px; border-radius: 999px;
      cursor: pointer; transition: all 0.12s;
    }
    .crm-tag-chip:hover { background: #fee2e2; color: #991b1b; }
    .crm-tag-chip:hover .crm-tag-x { opacity: 1; }
    .crm-tag-x { opacity: 0.5; font-size: 12px; line-height: 1; }
    .crm-tag-add {
      background: transparent; border: 1px dashed var(--c-line);
      color: var(--c-muted); font-size: 11px; font-weight: 600;
      padding: 2px 8px; border-radius: 999px; cursor: pointer;
      transition: all 0.12s; font-family: inherit;
    }
    .crm-tag-add:hover { border-color: var(--c-teal); color: var(--c-teal); background: var(--c-teal-soft); }
    .crm-tag-sugerido {
      background: var(--c-paper-2); border: 1px solid var(--c-line);
      color: var(--c-ink); font-size: 11px; font-weight: 600;
      padding: 3px 9px; border-radius: 999px; cursor: pointer;
      transition: all 0.12s; font-family: inherit;
    }
    .crm-tag-sugerido:hover { background: var(--c-teal-soft); color: #0e7490; border-color: var(--c-teal); }

    .crm-popover-wrap { position: relative; }
    .crm-popover {
      position: absolute; top: calc(100% + 6px); right: 0;
      background: #fff; border: 1px solid var(--c-line); border-radius: 10px;
      box-shadow: 0 8px 24px rgba(15,27,54,0.12), 0 2px 6px rgba(15,27,54,0.06);
      padding: 10px; z-index: 100; min-width: 220px;
      animation: crmPopIn 0.15s ease-out;
    }
    @keyframes crmPopIn {
      from { opacity: 0; transform: translateY(-4px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .crm-popover-tags { left: 0; right: auto; min-width: 260px; }
    .crm-popover-label {
      font-size: 10px; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.08em; color: var(--c-muted); margin-bottom: 6px;
    }
    .crm-popover-input {
      width: 100%; padding: 7px 10px; border: 1px solid var(--c-line);
      border-radius: 6px; font-size: 13px; font-family: inherit;
      margin-bottom: 10px; box-sizing: border-box;
    }
    .crm-popover-input:focus { outline: none; border-color: var(--c-teal); box-shadow: var(--shadow-focus); }
    .crm-popover-tags-grid { display: flex; flex-wrap: wrap; gap: 5px; }
    .crm-popover-row {
      display: flex; align-items: center; gap: 8px;
      padding: 7px 8px; border-radius: 6px;
      background: transparent; border: 0; width: 100%; text-align: left;
      font-size: 13px; color: var(--c-ink); cursor: pointer;
      font-family: inherit; transition: background 0.12s;
    }
    .crm-popover-row:hover { background: var(--c-paper-2); }
    .crm-popover-row.is-current { background: var(--c-teal-soft); font-weight: 600; }
    .crm-popover-role {
      margin-left: auto; font-size: 10px; color: var(--c-muted);
      text-transform: uppercase; letter-spacing: 0.06em;
    }
    .crm-popover-textarea {
      width: 100%; box-sizing: border-box; padding: 8px 10px;
      border: 1px solid var(--c-line); border-radius: 6px;
      font-size: 13px; font-family: inherit; resize: vertical;
      min-height: 90px;
    }
    .crm-popover-textarea:focus { outline: none; border-color: var(--c-teal); box-shadow: var(--shadow-focus); }
    .crm-popover-notas { min-width: 320px; }
    .crm-popover-actions {
      display: flex; gap: 6px; justify-content: flex-end; margin-top: 8px;
    }
    .crm-popover-btn-ghost,
    .crm-popover-btn-primary {
      padding: 6px 12px; border-radius: 6px; font-size: 12px; font-weight: 700;
      cursor: pointer; font-family: inherit; border: 0; transition: all 0.12s;
    }
    .crm-popover-btn-ghost { background: transparent; color: var(--c-muted); }
    .crm-popover-btn-ghost:hover { background: var(--c-paper-2); color: var(--c-ink); }
    .crm-popover-btn-primary { background: var(--c-teal); color: #fff; }
    .crm-popover-btn-primary:hover { background: #008a95; }
    .crm-popover-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Asignee button */
    .crm-assignee-btn {
      width: 32px; height: 32px; border-radius: 50%;
      background: var(--c-paper-2); border: 1px dashed var(--c-line);
      cursor: pointer; padding: 0;
      display: flex; align-items: center; justify-content: center;
      transition: all 0.12s;
    }
    .crm-assignee-btn:hover { border-style: solid; border-color: var(--c-teal); }
    .crm-assignee-btn.is-assigned { border: 0; background: transparent; }
    .crm-assignee-avatar {
      width: 28px; height: 28px; border-radius: 50%;
      background: linear-gradient(135deg, var(--c-navy-2), var(--c-teal));
      color: #fff; font-size: 11px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      letter-spacing: -0.02em;
    }
    .crm-assignee-empty { color: var(--c-muted); font-size: 16px; line-height: 1; }

    /* Estado button */
    .crm-estado-btn {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 5px 11px; border-radius: 999px;
      background: #fff; border: 1.5px solid;
      font-size: 11px; font-weight: 700; letter-spacing: 0.02em;
      cursor: pointer; font-family: inherit; transition: all 0.12s;
    }
    .crm-estado-btn:hover { background: var(--c-paper-2); }
    .crm-estado-dot { width: 8px; height: 8px; border-radius: 50%; }
    .crm-popover-estado { min-width: 160px; }

    /* Notas icon dot */
    .crm-icon-btn { position: relative; }
    .crm-icon-btn.has-content { color: var(--c-teal); }
    .crm-icon-dot {
      position: absolute; top: 5px; right: 5px;
      width: 7px; height: 7px; border-radius: 50%;
      background: var(--c-warning);
      border: 1.5px solid #fff;
    }

    /* ─────────── Responsive ─────────── */
    /* Fix: estos breakpoints apuntaban a .crm-wrap pero el grid real es .crm-body */
    @media (max-width: 1200px) {
      .crm-body { grid-template-columns: 300px 1fr 300px; }
    }
    @media (max-width: 1024px) {
      .crm-body { grid-template-columns: 280px 1fr; }
      .crm-cliente { display: none; }
    }
    /* Mobile: una sola columna por vez — inbox por default, chat cuando hay activa.
       Estilo WhatsApp: chat ocupa toda la pantalla, header con back, sin distracciones. */
    @media (max-width: 768px) {
      /* Maximizar espacio: ocultar topbar admin + sidebar + nav móvil cuando estamos en CRM */
      body.crm-fullscreen-mode .al-shell-topbar { display: none !important; }
      body.crm-fullscreen-mode .al-mobile-bar { display: none !important; }
      body.crm-fullscreen-mode .al-sidebar { display: none !important; }
      body.crm-fullscreen-mode .al-main { height: 100vh !important; max-height: 100vh !important; }

      /* Topbar del CRM colapsado */
      .crm-topbar { padding: 8px 10px; flex-wrap: nowrap; gap: 6px; min-height: 0; }
      .crm-topbar-title h1 { font-size: 15px; margin: 0; }
      .crm-topbar-tag { display: none; }
      .crm-topbar-stats { display: none; }  /* ocultar stats en mobile, no caben */
      .crm-topbar-cta { padding: 6px 10px !important; font-size: 12px; }

      /* Si hay conv activa, ocultar el topbar del CRM también (chat fullscreen tipo WA) */
      .crm-body.has-active ~ .crm-topbar,
      body.crm-fullscreen-mode .crm-body.has-active + .crm-topbar { display: none; }

      /* Body: 1 columna */
      .crm-body { grid-template-columns: 1fr; height: 100%; }
      .crm-body .crm-chat { display: none; }
      .crm-body.has-active .crm-inbox { display: none; }
      .crm-body.has-active .crm-chat { display: flex; flex-direction: column; height: 100%; }

      /* Botón ← prominente en header del chat */
      .crm-back {
        display: inline-flex !important; align-items: center; justify-content: center;
        margin-right: 6px; padding: 4px 10px; font-size: 22px;
        background: transparent; border: 0; color: #1a3461; cursor: pointer;
        min-width: 36px; height: 36px;
      }
      .crm-back:hover { background: rgba(0,0,0,0.05); border-radius: 50%; }

      /* Chat ajustes mobile */
      .crm-chat-head { padding: 8px 12px; position: sticky; top: 0; z-index: 10; background: #fff; border-bottom: 1px solid #e2e8f0; }
      .crm-chat-head-avatar { width: 36px; height: 36px; font-size: 13px; }
      .crm-chat-head-name { font-size: 15px; }
      .crm-chat-head-actions { gap: 4px; }
      .crm-chat-head-actions button { padding: 4px 6px; font-size: 13px; }
      .crm-msg-wrap { max-width: 85%; }
      .crm-mensajes { padding: 8px 10px; }
      .crm-composer { padding: 8px 10px; }

      /* Inbox compacta */
      .crm-inbox-head { padding: 8px 12px; gap: 6px; }
      .crm-inbox-list { padding: 4px 6px; }
      .crm-inbox-item { padding: 8px; }

      /* Ficha cliente: nunca visible en mobile */
      .crm-cliente { display: none !important; }
    }

    /* Botón back en desktop: oculto (ya hay sidebar visible) */
    @media (min-width: 769px) {
      .crm-back { display: none; }
    }
  `}</style>
}

/* ─────────── Modal: + Nuevo chat (template WA aprobada) ─────────── */
function ModalNuevoChat({ onClose, onCreado }) {
  const [paso, setPaso] = useState('contacto') // contacto | template | params
  const [contactos, setContactos] = useState([])
  const [busqueda, setBusqueda] = useState('')
  const [contactoSel, setContactoSel] = useState(null)
  const [templates, setTemplates] = useState([])
  const [templateSel, setTemplateSel] = useState(null)
  const [params, setParams] = useState([])
  const [loading, setLoading] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState(null)
  const [nombreUsuarioLogueado, setNombreUsuarioLogueado] = useState('')
  const [creandoTemplates, setCreandoTemplates] = useState(false)
  const [msgTemplates, setMsgTemplates] = useState(null)

  // Cargar contactos + nombre del agente logueado al abrir
  useEffect(() => {
    let alive = true
    async function load() {
      const [{ data: contactosData }, { data: { user } }] = await Promise.all([
        supabase.from('crm_contactos')
          .select('id, nombre, telefono_e164, email, tipos, cliente_id')
          .not('telefono_e164', 'is', null)
          .order('nombre')
          .limit(500),
        supabase.auth.getUser(),
      ])
      if (!alive) return
      setContactos(contactosData || [])
      if (user?.id) {
        const { data: prof } = await supabase
          .from('profiles')
          .select('nombre')
          .eq('id', user.id)
          .maybeSingle()
        if (alive) setNombreUsuarioLogueado((prof?.nombre || '').split(' ')[0] || 'Equipo Limpack')
      }
    }
    load()
    return () => { alive = false }
  }, [])

  // Cargar templates aprobadas cuando se llega al paso template
  useEffect(() => {
    if (paso !== 'template' || templates.length > 0) return
    let alive = true
    setLoading(true)
    supabase.functions.invoke('wa-list-templates').then(({ data, error: e }) => {
      if (!alive) return
      if (e) { setError(e.message); setLoading(false); return }
      if (data?.error) { setError(data.error); setLoading(false); return }
      const aprobadas = (data?.templates || []).filter(t => t.status === 'APPROVED')
      setTemplates(aprobadas)
      setLoading(false)
    })
    return () => { alive = false }
  }, [paso, templates.length])

  const contactosFiltrados = contactos.filter(c => {
    if (!busqueda.trim()) return true
    const q = busqueda.toLowerCase()
    return (c.nombre || '').toLowerCase().includes(q)
      || (c.telefono_e164 || '').includes(busqueda)
      || (c.email || '').toLowerCase().includes(q)
  })

  function elegirContacto(c) {
    setContactoSel(c)
    setPaso('template')
    setError(null)
  }
  function elegirTemplate(t) {
    setTemplateSel(t)
    // Precarga inteligente: si la template tiene 1 o 2 variables, asumimos {{1}}=nombre contacto, {{2}}=nombre agente
    const nombreContacto = (contactoSel?.nombre || '').split(' ')[0] || ''
    const nombreAgente = nombreUsuarioLogueado
    const precargados = Array.from({ length: t.variables_count }, (_, i) => {
      if (i === 0) return nombreContacto
      if (i === 1) return nombreAgente
      return ''
    })
    setParams(precargados)
    setPaso('params')
    setError(null)
  }

  // Vista previa: reemplaza {{1}}, {{2}}... con los params actuales
  const previewBody = templateSel
    ? templateSel.body.replace(/\{\{(\d+)\}\}/g, (_, n) => params[parseInt(n, 10) - 1] || `{{${n}}}`)
    : ''

  async function crearTemplatesEnMeta() {
    setCreandoTemplates(true)
    setMsgTemplates(null)
    try {
      const { data, error: e } = await supabase.functions.invoke('wa-create-template', { body: {} })
      if (e) throw new Error(e.message)
      const okCount = (data?.results || []).filter(r => r.ok).length
      const errCount = (data?.results || []).filter(r => !r.ok).length
      const detalleErrores = (data?.results || []).filter(r => !r.ok).map(r => `${r.name}: ${r.response?.error?.message || 'error'}`).join(' · ')
      setMsgTemplates(
        errCount === 0
          ? `✓ ${okCount} template(s) enviadas a Meta para aprobación. Refrescá en 5-10 min.`
          : `${okCount} OK · ${errCount} con error: ${detalleErrores}`
      )
      // Forzar refresh de la lista (próxima vez que se entre al paso template)
      setTemplates([])
    } catch (err) {
      setMsgTemplates('Error: ' + (err.message || String(err)))
    } finally {
      setCreandoTemplates(false)
    }
  }

  async function enviar() {
    if (!contactoSel || !templateSel) return
    if (templateSel.variables_count > 0 && params.some(p => !p.trim())) {
      setError('Completá todos los parámetros antes de enviar')
      return
    }
    setEnviando(true)
    setError(null)
    try {
      const { data, error: e } = await supabase.functions.invoke('wa-send-template', {
        body: {
          to: contactoSel.telefono_e164,
          template_name: templateSel.name,
          lang: templateSel.language,
          parameters: params,
          contacto_id: contactoSel.id,
          cliente_id: contactoSel.cliente_id || null,
          body_text_preview: previewBody,
        },
      })
      if (e) throw new Error(e.message)
      if (data?.error) throw new Error(data.error)
      onCreado(data.conversation_id)
    } catch (err) {
      setError(err.message || String(err))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div onClick={onClose} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={modalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, color: '#1a3461', fontSize: 18 }}>+ Iniciar chat por WhatsApp</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 22, color: '#94a3b8', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Stepper */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>
          <span style={paso === 'contacto' ? stepActivo : step}>1. Contacto</span>
          <span style={paso === 'template' ? stepActivo : (templateSel ? stepDone : step)}>2. Template</span>
          <span style={paso === 'params' ? stepActivo : step}>3. Revisar y enviar</span>
        </div>

        {/* PASO 1: Contacto */}
        {paso === 'contacto' && (
          <>
            <input
              type="search" autoFocus
              placeholder="Buscar por nombre, teléfono o email…"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              style={inpModal}
            />
            <div style={{ maxHeight: 360, overflowY: 'auto', marginTop: 10, border: '1px solid #e2e8f0', borderRadius: 8 }}>
              {contactosFiltrados.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  Sin contactos con teléfono cargado.
                </div>
              )}
              {contactosFiltrados.slice(0, 50).map(c => (
                <button
                  key={c.id} onClick={() => elegirContacto(c)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', background: '#fff', border: 0, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontFamily: 'inherit' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <div style={{ fontWeight: 700, color: '#1a3461', fontSize: 13 }}>{c.nombre || c.telefono_e164}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    📱 {c.telefono_e164}
                    {c.tipos && c.tipos.length > 0 && ` · ${c.tipos.join(', ')}`}
                  </div>
                </button>
              ))}
              {contactosFiltrados.length > 50 && (
                <div style={{ padding: 8, textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>
                  +{contactosFiltrados.length - 50} más. Refiná la búsqueda.
                </div>
              )}
            </div>
          </>
        )}

        {/* PASO 2: Template */}
        {paso === 'template' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                📞 <strong style={{ color: '#1a3461' }}>{contactoSel.nombre || contactoSel.telefono_e164}</strong>
                <button onClick={() => setPaso('contacto')} style={linkBtn}>cambiar</button>
              </div>
              <button
                onClick={crearTemplatesEnMeta}
                disabled={creandoTemplates}
                title="Crea/actualiza las templates Limpack en Meta. Cada template envía aprobación; tarda 5-10 min."
                style={{ padding: '4px 10px', background: '#fff', border: '1px solid #cbd5e1', color: '#475569', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: creandoTemplates ? 'wait' : 'pointer', fontFamily: 'inherit' }}
              >{creandoTemplates ? '⏳ Creando…' : '🔧 Sincronizar templates con Meta'}</button>
            </div>
            {msgTemplates && (
              <div style={{ padding: 10, background: msgTemplates.startsWith('✓') ? '#dcfce7' : '#fee2e2', border: `1px solid ${msgTemplates.startsWith('✓') ? '#86efac' : '#fca5a5'}`, borderRadius: 8, fontSize: 12, marginBottom: 10, color: msgTemplates.startsWith('✓') ? '#166534' : '#991b1b' }}>
                {msgTemplates}
              </div>
            )}
            {loading && <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>Cargando templates aprobadas…</div>}
            {!loading && templates.length === 0 && (
              <div style={{ padding: 20, background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                No hay templates aprobadas todavía. Hacé click en <strong>"🔧 Sincronizar templates con Meta"</strong> arriba para enviarlas a aprobación. Tarda 5-10 min hasta que aparezcan acá.
              </div>
            )}
            {!loading && templates.map(t => (
              <button
                key={t.id} onClick={() => elegirTemplate(t)}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#00A8B5'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <strong style={{ color: '#1a3461', fontSize: 13 }}>{t.name}</strong>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <span style={{ fontSize: 10, padding: '2px 8px', background: '#dcfce7', color: '#166534', borderRadius: 999, fontWeight: 700 }}>APPROVED</span>
                    <span style={{ fontSize: 10, padding: '2px 8px', background: '#e0e7ff', color: '#3730a3', borderRadius: 999, fontWeight: 700 }}>{t.language}</span>
                    {t.variables_count > 0 && (
                      <span style={{ fontSize: 10, padding: '2px 8px', background: '#fef3c7', color: '#854d0e', borderRadius: 999, fontWeight: 700 }}>{t.variables_count} var</span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{t.body || '(sin cuerpo de mensaje)'}</div>
              </button>
            ))}
          </>
        )}

        {/* PASO 3: Params + Preview + Enviar */}
        {paso === 'params' && (
          <>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
              📞 <strong style={{ color: '#1a3461' }}>{contactoSel.nombre || contactoSel.telefono_e164}</strong>
              {' · '}
              📋 <strong style={{ color: '#1a3461' }}>{templateSel.name}</strong>
              <button onClick={() => setPaso('template')} style={linkBtn}>cambiar template</button>
            </div>

            {templateSel.variables_count > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                  Parámetros ({templateSel.variables_count})
                </div>
                {Array.from({ length: templateSel.variables_count }).map((_, i) => {
                  const label = i === 0 ? 'Nombre del contacto' : i === 1 ? 'Tu nombre (agente)' : `Variable {{${i + 1}}}`
                  return (
                    <div key={i} style={{ marginBottom: 6 }}>
                      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#94a3b8', marginBottom: 3 }}>{`{{${i + 1}}} — ${label}`}</label>
                      <input
                        type="text"
                        placeholder={label}
                        value={params[i] || ''}
                        onChange={e => { const arr = [...params]; arr[i] = e.target.value; setParams(arr) }}
                        style={inpModal}
                      />
                    </div>
                  )
                })}
              </div>
            )}

            <div style={{ padding: 12, background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#166534', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                Vista previa (lo que verá el cliente)
              </div>
              <div style={{ fontSize: 13, color: '#1a3461', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {previewBody}
              </div>
            </div>

            <div style={{ padding: 10, background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, fontSize: 11, color: '#92400e', marginBottom: 12 }}>
              ⚠ Una vez enviado el template, se abre una ventana de 24 horas en la que podés responderle libremente. Si no contesta, después tenés que usar otra template.
            </div>
          </>
        )}

        {error && <div style={{ padding: 10, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {paso === 'params' && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onClose} disabled={enviando} style={btnSecondModal}>Cancelar</button>
            <button onClick={enviar} disabled={enviando} style={btnPrimaryModal}>
              {enviando ? '⏳ Enviando…' : '📤 Enviar mensaje'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const modalBg = { position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflowY: 'auto' }
const modalCard = { background: '#fff', borderRadius: 12, padding: 20, maxWidth: 560, width: '100%', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', marginTop: 30 }
const inpModal = { width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 7, fontSize: 13, color: '#1a3461', fontFamily: 'inherit', boxSizing: 'border-box' }
const step = { padding: '4px 10px', background: '#f1f5f9', borderRadius: 999 }
const stepActivo = { padding: '4px 10px', background: '#1a3461', color: '#fff', borderRadius: 999 }
const stepDone = { padding: '4px 10px', background: '#dcfce7', color: '#166534', borderRadius: 999 }
const btnPrimaryModal = { padding: '9px 18px', background: '#1a3461', color: '#fff', border: 0, borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }
const btnSecondModal = { padding: '9px 16px', background: 'transparent', border: '1px solid #cbd5e1', color: '#475569', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }
const linkBtn = { marginLeft: 6, background: 'transparent', border: 0, color: '#00A8B5', textDecoration: 'underline', fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }
